/*
 * Event wiring: the delegates are registered once on the never-torn-down #app
 * root at startup (page-lifetime), so their disposers need not be captured, and
 * the data-action selectors are intentional ad-hoc strings for this single shell.
 * Both are the documented opt-outs for these kerf lints.
 */
/* eslint-disable kerfjs/require-delegate-disposer, kerfjs/prefer-attr-selector */
import { openPopupMenuAt, type PopupMenuElement } from '@kerfjs/ui/popup-menu';
import { isContentItemActivation, wireContentItems } from '@kerfjs/ui/wire-content-items';
import { wireTextFields } from '@kerfjs/ui/wire-text-fields';
import { wireToolbarVisibility } from '@kerfjs/ui/wire-toolbar-visibility';
import { delegate, delegateCapture, effect, mount } from 'kerfjs';

import { type DemandNote, notesInScope, unreadNotes } from '../shared/demand-notes.js';
import { wireLineEvidence } from './components/line-evidence-wiring.js';
import type {} from './data/api.js';
import { api } from './data/api.js';
import { openAssumptionsEditor, saveAssumptionsEditor } from './data/assumptions-editor.js';
/**
 * The SPA shell for Phase 1 so far: the first-sign-in "create your organization"
 * flow (F1), an empty Demands landing with an explainer, and the Settings pages.
 * Feature screens (demand workspace, analysis, chat) mount into the same shell in
 * their own tickets. State lives in module signals; structure re-renders on auth
 * and view changes, values bind directly (kerf idiom).
 */
import {
  acceptLibraryFact,
  addDocumentAnnotationClient,
  addIntakePastedText,
  addManualBenchmarkClient,
  addNoteClient,
  addQuoteLineAsLine,
  answerFormClient,
  applyChatCard,
  archiveLibraryDoc,
  assignDemandClient,
  bulkApplyDecisions,
  cancelDemandClient,
  cancelMultifileBatch,
  chatNewConversation,
  clearLineDecision,
  clearOrderClient,
  closeChatStream,
  closeLibraryFactDialog,
  closeProjectChat,
  confirmHsCodeClient,
  confirmIntake,
  createOrganizationChat,
  createProjectChat,
  deleteDocumentAnnotationClient,
  deleteDocumentsClient,
  deleteLibraryFact,
  deleteOrgClient,
  deleteReceiptClient,
  dismissChatCard,
  downloadLibraryRevision,
  duplicateDemandClient,
  exportOrgClient,
  failOrderClient,
  ignoreQuoteLineClient,
  loadAiUsage,
  loadAnalysisReport,
  loadConversation,
  loadDemands,
  loadLibrary,
  loadLibraryProjects,
  loadMe,
  loadMoreDemands,
  loadOrgSavings,
  loadProjectChats,
  loadQuoteMatrix,
  loadSettings,
  loadVendors,
  markNotesReadClient,
  mergeVendorsClient,
  moveDocumentAnnotationClient,
  moveDocumentsClient,
  newLibraryProject,
  openDemand,
  openDocumentClient,
  openIntake,
  openLibraryDocumentClient,
  openProjectChat,
  openQuestionsDialogClient,
  openVendor,
  promoteDocumentClient,
  reclassifyDocAsQuote,
  recordLineDecisions,
  recordOutcomeClient,
  reloadSourceEvidence,
  remapQuoteLineClient,
  removeLines,
  reopenDemandClient,
  reopenDemandIfShown,
  reopenShortClient,
  reopenWithUpdateClient,
  replaceDocumentClient,
  replaceLibraryRevision,
  requestLineClarificationClient,
  restoreDemandChat,
  retryQuotationExtractionClient,
  runAnalysisClient,
  saveAiBudget,
  saveAssumptions,
  saveChatTitle,
  saveFxCurrency,
  saveIntakeField,
  saveLibraryFact,
  saveLineItemEditor,
  saveNoteEditClient,
  saveOrderClient,
  saveProfile,
  saveProjectInfo,
  saveQuestionsDraftClient,
  saveQuoteDialog,
  saveReceiptClient,
  saveThresholds,
  searchSourceRowsForPicker,
  selectFxCurrency,
  sendChat,
  setDemandReopenedForChangesClient,
  setQuoteBasis,
  showLibraryRevisions,
  signOut,
  skipFormClient,
  stopActing,
  syncLibraryChatTarget,
  unmapQuoteLinesClient,
  unreadDemandNotes,
  uploadBackgroundFile,
  uploadIntakeFiles,
  uploadQuoteFiles,
  watchDemandSearch,
  withdrawDemandClient,
} from './data/loaders.js';
import {
  closeMemberDialog,
  inviteMember,
  openInviteDialog,
  openMemberDialog,
  removeMember,
  resendInvitation,
  saveMemberRoles,
} from './data/members.js';
import { dismissPricingPrompt } from './data/pricing-prompt.js';
import { closeSiteDialog, openSiteDialog, saveSite } from './data/sites.js';
import { assumptionsDialog, assumptionsSubmitting } from './data/state.js';
import {
  aiBudgetDialogOpen,
  aiSpendView,
  analysisReport,
  attachmentErrorsOpen,
  cancelledExpanded,
  chatActiveId,
  chatHistoryOpen,
  chatLiveActivity,
  chatLiveText,
  chatMessages,
  chatSortDirection,
  chatSortField,
  chatTitleEditing,
  clarifyDialog,
  closedChatsExpanded,
  contextMenu,
  type ContextMenuTarget,
  currencyEditCode,
  currencyPickerFilter,
  currencyPickerOpen,
  DECISION_NEEDS_PRICE,
  decisionReason,
  decisions,
  decisionVendor,
  deleteConfirmOpen,
  demands,
  demandSearch,
  demandSiteFilter,
  demandStatusFilter,
  descEditing,
  documentPreview,
  documentSelectionAnchor,
  draftsExpanded,
  editingNoteId,
  editorSegment,
  inspectorCollapsed,
  type IntakeDoc,
  libraryDocs,
  libraryFactDialogId,
  libraryProjects,
  libraryRevisions,
  libraryScope,
  librarySelectionAnchor,
  libraryTab,
  lineBenchmarkDialogOpen,
  lineDecisionDialog,
  lineEditorDialog,
  lineEvidenceDetailActive,
  lineEvidenceDialog,
  lineEvidenceMessage,
  lineEvidenceView,
  lineSelectionAnchor,
  matrixFocus,
  matrixSelection,
  me,
  neededByEditing,
  newChatDialogOpen,
  notice,
  orderDialog,
  orgSavings,
  questionsDialogOpen,
  quoteDialog,
  quoteMatrix,
  quoteTaxBasis,
  receiptDialog,
  reopenUpdateOpen,
  returnDialogOpen,
  savingsBreakdownView,
  savingsPeriod,
  savingsSite,
  searchExpanded,
  selectedAnalysisId,
  selectedDocumentIds,
  selectedLibraryDocIds,
  selectedLineIds,
  settingsAreaCollapsed,
  settingsPage,
  settingsTab,
  shellSearch,
  shellSearchTokens,
  sidebarCollapsed,
  sourceRowPicker,
  summaryPane,
  titleEditing,
  vendorDetail,
  vendorDetailSection,
  vendorsOverview,
  view,
  workspace,
} from './data/state.js';
import {
  anyDialogHasUnsavedEdits,
  dialogPromptOpen,
  discardDialogEdits,
  installDialogGuard,
  keepEditingDialog,
  saveDialogEdits,
} from './data/unsaved-dialog-guard.js';
import {
  dirtySettingsPage,
  discardAndContinue,
  holdNavigation,
  keepEditing,
  saveAndContinue,
} from './data/unsaved-settings-guard.js';
import { addSupplierLeadAsVendor, closeVendorDialog, openVendorDialog, saveVendor } from './data/vendors.js';
import { actingReadOnly, installActingReadOnly } from './lib/acting-read-only.js';
import { cancelAppHistoryRestore, wireAppHistory } from './lib/app-history.js';
import { recommendedBatchDecisions } from './lib/batch-decision.js';
import {
  canOpenDemandSegment,
  currentCapabilities,
  currentDemandId,
  demandReadOnly,
  type SettingsTab,
  settingsTabForCapabilities,
} from './lib/context.js';
import { wireMountedDialogSurfaces } from './lib/dialog-surfaces.js';
import { shiftDocumentGalleryZoom } from './lib/document-gallery-zoom.js';
import { checked, elValue, field, selectValue, shareOrDownload } from './lib/dom.js';
import { editorTransferTarget } from './lib/editor-transfer-target.js';
import { documentReferenceMarkdown, isImageDocument, lineReferenceMarkdown } from './lib/entity-reference.js';
import { wireGalleryAnnotationDrag } from './lib/gallery-annotation-drag.js';
import { galleryArrowStep } from './lib/gallery-keyboard.js';
import { markInteraction } from './lib/interaction-generation.js';
import { activationKey, isNestedControl } from './lib/key-activation.js';
import { wireLazyRowMenus } from './lib/lazy-row-menus.js';
import { selectLine } from './lib/line-selection.js';
import {
  EMPTY_MATRIX_SELECTION,
  type MatrixDirection,
  type MatrixGrid,
  matrixGrid,
  type MatrixPosition,
  matrixPositionKey,
  moveMatrixFocus,
  parseMatrixPosition,
  reconcileMatrixSelection,
  type SelectionModifiers,
  selectMatrixTarget,
  targetAt,
} from './lib/matrix-selection.js';
import { loadString, saveBool, saveString } from './lib/prefs.js';
import { showAllLineRows, wireProgressiveLineRows } from './lib/progressive-line-rows.js';
import { savingsExportHref } from './lib/savings-export.js';
import { settingsLocation, type SettingsPage, settingsPageFor } from './lib/settings-nav.js';
import { showToast } from './lib/toast.js';
import { leavesSettingsPage } from './lib/unsaved-settings.js';
import { demandRowActions } from './views/shell/components/demand-sidebar-row.js';
import { wireProjectTabs } from './views/shell/components/project-tabs-header.js';
import { wireShellSearch } from './views/shell/components/search-field.js';
import { render, wireAppShell } from './views/shell/index.js';

/** Focus the demand-title input once the reactive render has swapped it in
 *  (autofocus alone won't steal focus from an already-focused button). */
function focusDemandTitle(root: HTMLElement): void {
  requestAnimationFrame(() => {
    root.querySelector<HTMLElement>('.toolbar-title-input[data-title="demand-title"] input')?.focus();
  });
}

function focusProjectChatTitle(root: HTMLElement): void {
  requestAnimationFrame(() => {
    root.querySelector<HTMLElement>('.toolbar-title-input[data-title="project-chat-title"] input')?.focus();
  });
}

/** Arrow keys that move the price matrix's focus (HS2-QV3276). */
const MATRIX_ARROWS: Partial<Record<string, MatrixDirection>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

export function init(): void {
  const root = document.getElementById('app');
  if (root === null) return;
  root.textContent = '';
  mount(root, render);
  installActingReadOnly();
  wireMountedDialogSurfaces(root);

  /** Open a context menu for `target` at the pointer (HS2-3KAZPW). The menu's DOM does not exist until now:
   *  setting the signal makes the shell render it, and once it is in the document it opens at the pointer;
   *  Kerf owns collision handling and focus. Closing removes it again. */
  let contextMenuCount = 0;
  const showContextMenu = (target: ContextMenuTarget, event: Event, at?: { x: number; y: number }): void => {
    // A keyboard-opened menu (Shift+F10 / the menu key) passes where to open it.
    const { x, y } = at ?? { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY };
    event.preventDefault();
    const id = ++contextMenuCount;
    contextMenu.value = { id, target, x, y };
    let frames = 0;
    const open = (): void => {
      const menu = root.querySelector<PopupMenuElement>(`[data-context-menu-id="${String(id)}"]`);
      if (menu !== null) openPopupMenuAt(menu, x, y);
      else if (++frames < 30 && contextMenu.value?.id === id) requestAnimationFrame(open);
    };
    requestAnimationFrame(open);
  };
  // Kerf's menu panel is a native popover (beta 106): its non-bubbling `toggle` to
  // `closed` is the menu closing, so listen in the capture phase. Submenus close too;
  // only the root panel's close removes the menu.
  root.addEventListener(
    'toggle',
    (event) => {
      if (event.newState !== 'closed') return;
      const panel = event.target;
      if (!(panel instanceof Element) || !panel.matches('.kui-popup-menu__menu:not([data-submenu])')) return;
      const menu = panel.closest('[data-context-menu-id]');
      if (menu !== null && contextMenu.value?.id === Number(menu.getAttribute('data-context-menu-id'))) {
        contextMenu.value = null;
      }
    },
    { capture: true },
  );

  // Unsaved settings edits (HS2-HV4R7C): a navigation click on a page with an explicit
  // Save is held in the capture phase, before its delegated handler runs, and replayed
  // once the viewer saves or discards in the unsaved-changes dialog.
  root.addEventListener(
    'click',
    (event) => {
      const el = (event.target as Element | null)?.closest<HTMLElement>('[data-action]');
      const action = el?.getAttribute('data-action');
      if (el === null || el === undefined || action === null || action === undefined) return;
      const leaves = leavesSettingsPage(
        action,
        { itemId: el.getAttribute('data-item-id'), segment: el.dataset.segmentValue },
        { area: settingsTab.value, page: settingsPage.value },
      );
      if (
        !leaves ||
        !holdNavigation(root, {
          proceed: () => {
            el.click();
          },
        })
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true },
  );
  // Unsaved dialog edits (HS2-FSBQVX): Cancel, Escape, or an outside press on a guarded
  // dialog with edits asks the same question; its answers go to the dialog guard.
  installDialogGuard(root);
  window.addEventListener('beforeunload', (event) => {
    if (dirtySettingsPage(root) !== null || anyDialogHasUnsavedEdits(root)) event.preventDefault();
  });
  const keepEditingEither = (): void => {
    if (dialogPromptOpen()) keepEditingDialog();
    else keepEditing();
  };
  delegate(root, 'click', '[data-action="unsaved-keep"]', (event) => {
    event.stopPropagation();
    keepEditingEither();
  });
  delegateCapture(root, 'close', 'dialog[data-action="unsaved-dialog"]', keepEditingEither);
  delegate(root, 'click', '[data-action="unsaved-discard"]', (event) => {
    event.stopPropagation();
    if (dialogPromptOpen()) discardDialogEdits();
    else discardAndContinue(root);
  });
  delegate(root, 'click', '[data-action="unsaved-save"]', (event) => {
    event.stopPropagation();
    if (dialogPromptOpen()) saveDialogEdits();
    else void saveAndContinue(root);
  });

  // Workspace reconciliation is allowed to commit only when no interaction
  // began after its request started. Capture the events that can create or
  // mutate transient UI state before delegated/component handlers run.
  for (const type of ['pointerdown', 'click', 'keydown', 'input']) {
    root.addEventListener(type, markInteraction, { capture: true });
  }
  // Line-row action menus are filled in on first open (HS2-D2G57R).
  wireLazyRowMenus(root);
  // Large line lists render their first screenful, then the rest in idle chunks (HS2-HVR0HY).
  wireProgressiveLineRows(root);

  // Chat auto-scroll (HS2-VPC1Z2): keep the transcript pinned to the bottom as
  // messages and streamed text arrive, unless the user has scrolled up; resume
  // when they scroll back within ~40px of the bottom. `scroll` does not bubble,
  // so listen in the capture phase; the #app root is page-lifetime.
  let chatStuckToBottom = true;
  let lastChatId = chatActiveId.value;
  // Ignore scroll events briefly after our own pin: a single programmatic
  // scrollTop can emit several scroll events, and a pin that lands a few px short
  // of content still laying out would otherwise flip the flag off for good.
  let ignoreChatScrollUntil = 0;
  const nearChatBottom = (el: HTMLElement): boolean => el.scrollHeight - el.scrollTop - el.clientHeight <= 40;
  // The transcript's scroller is the nearest scrolling ancestor of
  // `.chat-scroll-body`: the assistant rail's Pane content (HS2-AC9K4G) or the
  // work area's Pane content in the center Chat view (HS2-706Y60).
  const scrollerOf = (body: Element): HTMLElement | null => {
    for (let el = body.parentElement; el !== null; el = el.parentElement) {
      const overflow = getComputedStyle(el).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return el;
    }
    return null;
  };
  const isChatScroller = (el: HTMLElement): boolean => {
    const body = el.querySelector('.chat-scroll-body');
    return body !== null && scrollerOf(body) === el;
  };
  root.addEventListener(
    'scroll',
    (e) => {
      const el = e.target as HTMLElement;
      if (!(el instanceof HTMLElement) || !isChatScroller(el)) return;
      if (performance.now() < ignoreChatScrollUntil) return;
      chatStuckToBottom = nearChatBottom(el);
    },
    true,
  );
  const pinChatToBottom = (): void => {
    const body = root.querySelector('.chat-scroll-body');
    const el = body === null ? null : scrollerOf(body);
    if (el === null || !chatStuckToBottom) return;
    ignoreChatScrollUntil = performance.now() + 150;
    el.scrollTop = el.scrollHeight;
  };
  effect(() => {
    // Reading these subscribes the effect: it re-runs whenever the transcript
    // gains messages, streamed text, or live activity (any height change).
    const contentSize = chatMessages.value.length + chatLiveText.value.length + chatLiveActivity.value.length;
    const id = chatActiveId.value;
    if (id !== lastChatId) {
      chatStuckToBottom = true;
      lastChatId = id;
    } // a switched/new conversation opens at the bottom
    if (contentSize === 0) return;
    // Pin now, then again after layout settles — WA components and rendered
    // Markdown can grow a bubble a frame or two after the signal update.
    requestAnimationFrame(pinChatToBottom);
    setTimeout(pinChatToBottom, 80);
  });

  // Toolbar search wiring lives beside the field it wires (HS2-WCD4ZG).
  void wireShellSearch(root); // page-lifetime root
  // The demand list's shell search also runs on the server (HS2-X9K02S).
  void watchDemandSearch(); // page-lifetime
  delegate(root, 'click', '[data-action="demands-load-more"]', () => {
    void loadMoreDemands();
  });
  void wireAppShell(root); // page-lifetime root
  void wireLineEvidence(root); // page-lifetime root
  void wireGalleryAnnotationDrag(root, (id, point) => {
    void moveDocumentAnnotationClient(id, point);
  });
  // Enter/Space activation for interactive Kerf ContentItem cards — today the
  // editable Quotes vendor cards (HS2-CY4F20). Their data-action is handled below.
  void wireContentItems(root); // page-lifetime root
  void wireTextFields(root); // clear buttons, count and multiline growth
  void wireToolbarVisibility(root); // ToolbarText hideBelow thresholds (HS2-HJ806N), page-lifetime root
  void wireProjectTabs(root); // project settings tabs (HS2-91S2YF), page-lifetime root
  delegate(root, 'click', '[data-action="create-org"]', () => {
    void (async () => {
      notice.value = '';
      const tags = field(root, 'industryTags')
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '');
      const res = await api.createOrg({
        name: field(root, 'name'),
        country: field(root, 'country') || undefined,
        baseCurrency: field(root, 'baseCurrency') || undefined,
        industryTags: tags,
        siteName: field(root, 'siteName') || undefined,
        importCapable: checked(root, 'importCapable'),
      });
      if (res.ok) await loadMe();
      else notice.value = res.error ?? 'Could not create the organization.';
    })();
  });

  // Sign out, then reload `/` (the anonymous Sign In card) so no view keeps the
  // previous session's state (HS2-ZKA0HA).
  delegate(root, 'click', '[data-action="signout"]', () => {
    void signOut();
  });
  // Leave a member's read-only view for the operator console (HS2-RHZ75A).
  delegate(root, 'click', '[data-action="stop-acting"]', () => {
    void stopActing();
  });

  // Enter another organization (HS2-QZ4PAD). Every view holds org-scoped state,
  // so a full reload into the new session is the reset that cannot miss a signal.
  delegate(root, 'click', '[data-action="switch-org"], [data-action="choose-org"]', (e) => {
    const orgId = (e.target as Element).closest('[data-org-id]')?.getAttribute('data-org-id');
    if (orgId === null || orgId === undefined) return;
    void (async () => {
      const res = await api.switchOrg(orgId);
      if (!res.ok) {
        notice.value = res.error ?? 'Could not switch organization.';
        return;
      }
      closeChatStream();
      window.location.assign('/');
    })();
  });
  delegate(root, 'click', '[data-action="retry-org-choice"]', () => {
    void loadMe();
  });

  delegate(root, 'click', '[data-action="go-settings"]', () => {
    closeChatStream();
    view.value = 'settings';
    void loadSettings();
  });
  delegate(root, 'click', '[data-action="back-to-list"]', () => {
    closeChatStream();
    view.value = 'demands';
    void loadDemands();
  });

  // --- Navigation overhaul (HS2-BY1SX8) ---
  // Primary-view segmented control in the main-content toolbar.
  delegate(root, 'click', '[data-action="seg-view"]', (e) => {
    const seg = (e.target as Element).closest<HTMLElement>('[data-action="seg-view"]')?.dataset.segmentValue;
    if (seg === undefined || seg === '') return;
    cancelAppHistoryRestore();
    closeChatStream();
    shellSearch.value = '';
    shellSearchTokens.value = [];
    searchExpanded.value = false; // reset the view search
    // Returning to Demands restores the open demand's workspace (the sidebar still
    // highlights it), not the empty landing (HS2-5XAJ7H).
    switch (seg) {
      case 'demands':
        view.value = workspace.value !== null ? 'workspace' : 'demands';
        void loadDemands();
        if (workspace.value !== null) void restoreDemandChat();
        break;
      case 'chat':
        view.value = 'conversations';
        void loadProjectChats();
        break;
      case 'stats':
        view.value = 'savings';
        orgSavings.value = null;
        void loadOrgSavings();
        break;
      case 'settings':
        openSettingsArea(settingsTabForCapabilities(loadString('settingsTab'), currentCapabilities()));
        break;
    }
  });
  delegate(root, 'click', '[data-action="toggle-sidebar"]', () => {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    saveBool('sidebarCollapsed', sidebarCollapsed.value);
  });
  delegate(root, 'click', '[data-action="toggle-inspector"]', () => {
    inspectorCollapsed.value = !inspectorCollapsed.value;
    saveBool('inspectorCollapsed', inspectorCollapsed.value);
  });
  delegate(root, 'click', '[data-action="toggle-drafts"]', () => {
    draftsExpanded.value = !draftsExpanded.value;
  });
  delegate(root, 'click', '[data-action="toggle-cancelled"]', () => {
    cancelledExpanded.value = !cancelledExpanded.value;
  });
  // Demand title (in the main toolbar): click to edit, blur to stop + auto-save (HS2-HJT6JJ).
  delegate(root, 'click', '[data-action="edit-demand-title"]', () => {
    titleEditing.value = true;
    focusDemandTitle(root);
  });
  delegate(root, 'blur', '[data-action="demand-title-input"]', (e) => {
    titleEditing.value = false;
    void saveIntakeField('title', elValue(e.target as Element));
  });
  // Sidebar demand / thread selection (ListItem itemId → data-item-id).
  delegate(root, 'click', '[data-action="open-demand-nav"]', (e) => {
    const id = (e.target as Element).closest<HTMLElement>('[data-action="open-demand-nav"]')?.dataset.itemId;
    if (id !== undefined && id !== '') void openDemand(id);
  });
  // Right-click a sidebar demand row opens its context menu (HS2-3BVPEF); the menu's
  // Open / Cancel / Reopen items carry that row's own id.
  delegate(root, 'contextmenu', '.demand-sidebar-row', (e, el) => {
    const demandId = el.getAttribute('data-context-demand');
    if (demandId === null) return;
    showContextMenu(
      {
        kind: 'demand-row',
        demandId,
        cancelled: el.hasAttribute('data-context-cancelled'),
        actions: demandRowActions(demands.value.find((demand) => demand.id === demandId)),
      },
      e,
    );
  });
  // Right-click a chat row for Close / Reopen chat (HS2-3KAZPW).
  delegate(root, 'contextmenu', '.chat-sidebar-row', (e, el) => {
    const chatId = el.getAttribute('data-context-chat');
    if (chatId === null) return;
    showContextMenu({ kind: 'chat-row', chatId, closed: el.hasAttribute('data-context-closed') }, e);
  });
  delegate(root, 'click', '[data-action="demand-open-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id !== null && id !== '') void openDemand(id);
  });
  delegate(root, 'click', '[data-action="demand-cancel-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id !== null && id !== '') void cancelDemandClient(id);
  });
  // Assign to Me / Assign To / Unassign from the row menu (FR-5.20, HS2-C63T9N). Each item carries
  // the assignee it saw; an empty id means none.
  delegate(root, 'click', '[data-action="demand-assign-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id === null || id === '') return;
    const assignee = el.getAttribute('data-assignee-id') ?? '';
    const expected = el.getAttribute('data-expected-assignee-id') ?? '';
    void assignDemandClient(id, assignee === '' ? null : assignee, expected === '' ? null : expected);
  });
  delegate(root, 'click', '[data-action="demand-reopen-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id !== null && id !== '') void reopenDemandClient(id);
  });
  delegate(root, 'click', '[data-action="open-thread-nav"]', (e) => {
    const id = (e.target as Element).closest<HTMLElement>('[data-action="open-thread-nav"]')?.dataset.itemId;
    if (id !== undefined && id !== '') void openProjectChat(id);
  });
  // Close / reopen a project chat and collapse the Closed Chats group (HS2-AEX5AM).
  delegate(root, 'click', '[data-action="toggle-closed-chats"]', () => {
    closedChatsExpanded.value = !closedChatsExpanded.value;
  });
  delegate(root, 'click', '[data-action="close-project-chat"]', (_e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null && id !== '') void closeProjectChat(id, true);
  });
  delegate(root, 'click', '[data-action="reopen-project-chat"]', (_e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null && id !== '') void closeProjectChat(id, false);
  });
  // Chat sort is a popup menu now (HS2-MXAYM2): each option is a ListItem whose
  // item-id sets the field / direction explicitly.
  delegate(root, 'click', '[data-action="chat-sort-field"]', (_e, el) => {
    const value = el.getAttribute('data-item-id');
    if (value === 'created' || value === 'updated') chatSortField.value = value;
  });
  delegate(root, 'click', '[data-action="chat-sort-direction"]', (_e, el) => {
    const value = el.getAttribute('data-item-id');
    if (value === 'asc' || value === 'desc') chatSortDirection.value = value;
  });
  // New chat picks its project in a dialog (HS2-1E0YTJ).
  delegate(root, 'click', '[data-action="new-chat-open"]', () => {
    newChatDialogOpen.value = true;
    // Offer the current projects, including one made since Chat opened (HS2-9AW3T9).
    void loadLibraryProjects();
  });
  delegate(root, 'click', '[data-action="new-organization-chat"]', () => {
    newChatDialogOpen.value = false;
    void createOrganizationChat().then((created) => {
      if (created) focusProjectChatTitle(root);
    });
  });
  delegateCapture(root, 'close', 'dialog[data-action="new-chat-dialog"]', () => {
    newChatDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="new-chat-pick"]', (_e, el) => {
    const projectId = el.getAttribute('data-item-id');
    if (projectId === null || projectId === '') return;
    newChatDialogOpen.value = false;
    void createProjectChat(projectId).then((created) => {
      if (created) focusProjectChatTitle(root);
    });
  });
  delegate(root, 'click', '[data-action="edit-project-chat-title"]', () => {
    chatTitleEditing.value = true;
    focusProjectChatTitle(root);
  });
  delegate(root, 'blur', '[data-action="project-chat-title-input"]', (_e, el) => {
    chatTitleEditing.value = false;
    void saveChatTitle(elValue(el));
  });
  // Settings navigation (HS2-CJM2YK, HS2-91S2YF). Every settings destination goes
  // through openSettingsPage, which remembers the area and each area's page locally.
  // Knowledge Base and Vendors keep their own views (the library chat and loaders
  // depend on them); project pages act on the project chosen in the project tabs.
  const openSettingsPage = (area: SettingsTab, page: SettingsPage): void => {
    closeChatStream();
    shellSearch.value = '';
    shellSearchTokens.value = [];
    searchExpanded.value = false;
    settingsTab.value = area;
    settingsPage.value = page;
    settingsAreaCollapsed.value = false;
    saveString('settingsTab', area);
    saveString(`settingsPage.${area}`, page);
    if (area === 'organization' && page === 'knowledge-base') {
      libraryScope.value = '';
      libraryTab.value = 'documents';
      view.value = 'library';
      void loadLibrary();
      void syncLibraryChatTarget();
    } else if (area === 'organization' && page === 'vendors') {
      view.value = 'vendors';
      vendorsOverview.value = null;
      vendorDetail.value = null;
      void loadVendors();
    } else if (area === 'project') {
      void openProjectPage(page);
    } else {
      view.value = 'settings';
      void loadSettings();
    }
  };
  // A project page needs a selected project: keep the current one, else the first.
  const openProjectPage = async (page: SettingsPage): Promise<void> => {
    if (!libraryProjects.value.some((project) => project.id === libraryScope.value)) {
      if (libraryProjects.value.length === 0) await loadLibraryProjects();
      libraryScope.value = libraryProjects.value[0]?.id ?? '';
    }
    if (page === 'knowledge-base' && libraryScope.value !== '') {
      libraryTab.value = 'documents';
      view.value = 'library';
      void loadLibrary();
      void syncLibraryChatTarget();
    } else {
      settingsPage.value = page;
      view.value = 'settings';
    }
  };
  const openSettingsArea = (area: SettingsTab): void => {
    openSettingsPage(area, settingsPageFor(area, loadString(`settingsPage.${area}`)));
  };
  delegate(root, 'click', '[data-action="settings-area"]', (_e, el) => {
    const area = el.getAttribute('data-item-id');
    if (area !== 'organization' && area !== 'project' && area !== 'personal') return;
    const location = settingsLocation(view.value, {
      area: settingsTab.value,
      page: settingsPage.value,
      libraryScope: libraryScope.value,
    });
    // The open area's header folds it away (and back); another area opens on its last page.
    if (location?.area === area) settingsAreaCollapsed.value = !settingsAreaCollapsed.value;
    else openSettingsArea(area);
  });
  delegate(root, 'click', '[data-action="settings-page"]', (_e, el) => {
    const [area, page] = (el.getAttribute('data-item-id') ?? '').split(':');
    if (area !== 'organization' && area !== 'project' && area !== 'personal') return;
    openSettingsPage(area, settingsPageFor(area, page));
  });
  delegate(root, 'click', '[data-action="settings-project"]', (_e, el) => {
    const id = el.getAttribute('data-tab-id');
    if (id === null || id === libraryScope.value) return;
    libraryScope.value = id;
    if (view.value === 'library') {
      libraryTab.value = 'documents';
      void loadLibrary();
      void syncLibraryChatTarget();
    }
  });
  delegate(root, 'change', '[data-action="project-info-name"], [data-action="project-info-description"]', (_e, el) => {
    const field = el.getAttribute('data-action') === 'project-info-name' ? 'name' : 'description';
    void saveProjectInfo(libraryScope.value, field, elValue(el));
  });
  delegate(root, 'click', '[data-action="go-settings-org"]', () => {
    openSettingsArea('organization');
  });
  delegate(root, 'click', '[data-action="go-settings-project"]', () => {
    openSettingsArea('project');
  });
  delegate(root, 'click', '[data-action="go-settings-personal"]', () => {
    openSettingsArea('personal');
  });
  delegate(root, 'click', '[data-action="go-library-org"]', () => {
    openSettingsPage('organization', 'knowledge-base');
  });
  delegate(root, 'click', '[data-action="go-library-project"]', () => {
    openSettingsPage('project', 'knowledge-base');
  });

  // --- Demand AI chat (HS2-ZXJJSS) ---
  delegate(root, 'click', '[data-action="chat-send"]', () => void sendChat(root));
  delegate(root, 'click', '[data-action="chat-new"]', () => void chatNewConversation());
  delegate(root, 'click', '[data-action="chat-history"]', () => {
    chatHistoryOpen.value = !chatHistoryOpen.value;
  });
  delegate(root, 'click', '[data-action="chat-select"]', (e, el) => {
    const cid = el.getAttribute('data-item-id');
    if (cid !== null) {
      chatHistoryOpen.value = false;
      void loadConversation(cid);
    }
  });
  delegate(root, 'click', '[data-chat-apply]', (e, el) => {
    const spec = el.getAttribute('data-chat-apply');
    if (spec !== null) void applyChatCard(spec);
  });
  delegate(root, 'click', '[data-chat-dismiss]', (e, el) => {
    const spec = el.getAttribute('data-chat-dismiss');
    if (spec !== null) void dismissChatCard(spec);
  });
  // Enter sends (Shift+Enter inserts a newline).
  delegate(root, 'keydown', '[name="chat-input"]', (e) => {
    const ev = e as KeyboardEvent;
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void sendChat(root);
    }
  });

  delegate(root, 'click', '[data-action="new-demand"]', () => {
    void (async () => {
      const orgId = me.value?.organizationId;
      if (orgId === null || orgId === undefined) return;
      // Start a draft and open the intake screen (Flow F2). Title is refined there.
      const res = await api.createDemand(orgId, { title: 'Untitled demand', asDraft: true });
      if (res.ok && res.data !== null) {
        await loadDemands(); // the draft must appear in the always-visible master list
        await openIntake(res.data.id);
        titleEditing.value = true; // a new demand opens with the title focused (HS2-HJT6JJ)
        focusDemandTitle(root);
      }
    })();
  });

  // --- Intake (Flow F2) ---
  // The sub-header's Attach button opens its group's hidden file input (HS2-NHDJXB).
  delegate(root, 'click', '[data-action="attach-open"]', (_e, el) => {
    el.parentElement?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  });
  delegate(root, 'change', '[data-intake-upload]', (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>('[data-intake-upload]');
    if (input === null) return;
    const kind = input.getAttribute('data-intake-upload') ?? 'supporting';
    const files = input.files;
    if (files === null || files.length === 0) return;
    void uploadIntakeFiles(Array.from(files), kind);
  });
  // "This looks like a vendor quotation — add it as a quote?" (HS2-FKJB32)
  delegate(root, 'click', '[data-action="reclassify-quotation"]', (e) => {
    const docId = (e.target as Element).closest('[data-doc-id]')?.getAttribute('data-doc-id');
    if (docId !== null && docId !== undefined && docId !== '') void reclassifyDocAsQuote(docId);
  });
  delegate(root, 'click', '[data-action="intake-add-row"]', () => {
    if (demandReadOnly()) return;
    lineEditorDialog.value = { lineId: null };
  });
  // The whole editor content area is a file drop target on the upload segments
  // (HS2-17E1DF): one set of handlers on the Workbench work area, routing by the active
  // segment (its value: lines → demand, quotes → quotation, files → automatic classification).
  // Dragging highlights the whole area; dragleave clears only when the pointer
  // actually leaves the container.
  const uploadEditorFiles = (seg: string | null, files: File[]): void => {
    if (actingReadOnly()) return;
    const target = editorTransferTarget(seg);
    if (target === null) return;
    if (target.kind === 'quotation') void uploadQuoteFiles(files, target.kind);
    else void uploadIntakeFiles(files, target.kind);
  };
  const activeEditorTransferTarget = () =>
    (view.value === 'workspace' || view.value === 'intake') && workspace.value !== null
      ? editorTransferTarget(editorSegment.value, demandReadOnly())
      : null;
  const libraryDropActive = () => view.value === 'library' && !actingReadOnly();
  delegate(root, 'dragover', '[data-workbench-main]', (e) => {
    const box = (e.target as Element).closest<HTMLElement>('[data-workbench-main]');
    if (box === null || (activeEditorTransferTarget() === null && !libraryDropActive())) return;
    e.preventDefault();
    const area = box.querySelector<HTMLElement>('main[aria-label="Procurement Workspace"]') ?? box;
    const rect = area.getBoundingClientRect();
    const overlay = box.querySelector<HTMLElement>('.editor-drop-overlay');
    overlay?.style.setProperty('--editor-drop-top', `${String(rect.top)}px`);
    overlay?.style.setProperty('--editor-drop-left', `${String(rect.left)}px`);
    overlay?.style.setProperty('--editor-drop-width', `${String(rect.width)}px`);
    overlay?.style.setProperty('--editor-drop-height', `${String(rect.height)}px`);
    overlay?.classList.add('dragging');
  });
  delegate(root, 'dragleave', '[data-workbench-main]', (e) => {
    const box = (e.target as Element).closest('[data-workbench-main]');
    const related = (e as DragEvent).relatedTarget as Node | null;
    if (box !== null && (related === null || !box.contains(related)))
      box.querySelector('.editor-drop-overlay')?.classList.remove('dragging');
  });
  delegate(root, 'drop', '[data-workbench-main]', (e) => {
    const box = (e.target as Element).closest<HTMLElement>('[data-workbench-main]');
    if (box === null) return;
    const target = activeEditorTransferTarget();
    box.querySelector('.editor-drop-overlay')?.classList.remove('dragging');
    if (target === null && !libraryDropActive()) return;
    e.preventDefault();
    const files = (e as DragEvent).dataTransfer?.files;
    if (files !== undefined && files.length > 0) {
      if (libraryDropActive())
        void (async () => {
          for (const file of Array.from(files)) await uploadBackgroundFile(root, file);
        })();
      else if (target !== null) uploadEditorFiles(target.segment, Array.from(files));
    }
  });
  // The focused work surface accepts the same clipboard content as a drop. Its
  // focus ring makes that keyboard context visible (HS2-BT2HV7).
  delegate(root, 'paste', '[data-workbench-main]', (e) => {
    if (demandReadOnly()) return; // a cancelled demand takes no pasted lines (HS2-ZXQAM6)
    // Let an editable control own its clipboard event.
    if ((e.target as Element).closest('input, textarea, [contenteditable]') !== null) return;
    const target = activeEditorTransferTarget();
    if (target === null) return;
    const data = (e as ClipboardEvent).clipboardData;
    if (data === null) return;
    if (data.files.length > 0) {
      e.preventDefault();
      uploadEditorFiles(target.segment, Array.from(data.files));
      return;
    }
    const text = data.getData('text/plain');
    if (target.acceptsText && text.trim() !== '') {
      e.preventDefault();
      void addIntakePastedText(text);
    }
  });
  delegate(root, 'click', '[data-action="attachment-errors-open"]', () => {
    attachmentErrorsOpen.value = true;
  });
  delegate(root, 'click', '[data-action="attachment-error-retry-quotation"]', (event, button) => {
    if (demandReadOnly()) return;
    const docId = button.getAttribute('data-document-id');
    if (docId === null || docId === '') return;
    void retryQuotationExtractionClient(docId);
  });
  delegateCapture(root, 'close', 'dialog.attachment-errors-dialog', () => {
    attachmentErrorsOpen.value = false;
  });

  // Documents and quotes transfers are handled by the editor-wide target.

  // --- Line-items reading, selection, edit dialog, and actions ---
  const applyLineSelection = (id: string, event: MouseEvent | KeyboardEvent): void => {
    if (demandReadOnly()) return; // no selection on a cancelled demand (HS2-ZXQAM6)
    const orderedIds = ((workspace.value?.lineItems ?? []) as { id: string }[]).map((line) => line.id);
    const result = selectLine({
      orderedIds,
      selectedIds: selectedLineIds.value,
      clickedId: id,
      anchorId: lineSelectionAnchor.value,
      toggle: event.metaKey || event.ctrlKey,
      range: event.shiftKey,
    });
    selectedLineIds.value = result.selectedIds;
    lineSelectionAnchor.value = result.anchorId;
  };
  // Pointer and keyboard share one notion of a nested control (HS2-7V8NJ4).
  // The line is a Kerf multi-select ContentItem row (HS2-TYTAS6); Kerf's own
  // activation check also covers nested shadow-DOM controls such as a menu trigger.
  delegate(root, 'click', '[data-line-item]', (e, el) => {
    if (isNestedControl(e.target, el) || !isContentItemActivation(e, el as HTMLElement)) return;
    const id = el.getAttribute('data-line-item');
    if (id !== null) applyLineSelection(id, e as MouseEvent);
  });
  delegate(root, 'dblclick', '[data-line-item]', (e, el) => {
    // A read-only demand opens the same dialog for viewing (HS2-SECM6V); the dialog itself
    // renders read-only fields and no Save, and the save handler refuses to write.
    if (isNestedControl(e.target, el)) return;
    const id = el.getAttribute('data-line-item');
    if (id !== null) lineEditorDialog.value = { lineId: id };
  });
  // Capture phase, ahead of wireContentItems (which would turn both keys into the
  // selecting click): Enter edits, Space selects. Arrows/Home/End stay with Kerf,
  // which moves focus between rows (HS2-TYTAS6).
  delegateCapture(root, 'keydown', '[data-line-item]', (e, el) => {
    if (isNestedControl(e.target, el)) return;
    const key = (e as KeyboardEvent).key;
    const id = el.getAttribute('data-line-item');
    if (id === null || (key !== ' ' && key !== 'Enter')) return;
    e.preventDefault();
    // Enter opens the line (view-only on a read-only demand, HS2-SECM6V); Space selects, and
    // applyLineSelection already ignores it on a read-only demand.
    if (key === 'Enter') lineEditorDialog.value = { lineId: id };
    else applyLineSelection(id, e as KeyboardEvent);
  });
  // A clarification warning's Open Line (row or banner) opens that line and its notes (HS2-4YCV66).
  delegate(root, 'click', '[data-action="line-open"]', (e, el) => {
    const id = el.getAttribute('data-line-id');
    if (id !== null) lineEditorDialog.value = { lineId: id };
  });
  // Kebab "Remove" (single) and the batch Remove.
  delegate(root, 'click', '[data-action="line-remove"]', (e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null) void removeLines([id]);
  });
  delegate(root, 'click', '[data-action="line-copy-reference"]', (e, el) => {
    e.preventDefault();
    const demandId = el.getAttribute('data-reference-demand-id');
    const lineId = el.getAttribute('data-item-id');
    const lines = (workspace.value?.lineItems ?? []) as { id: string; lineNumber: number; description: string }[];
    const line = lines.find((candidate) => candidate.id === lineId);
    if (demandId === null || lineId === null || line === undefined) return;
    void navigator.clipboard
      .writeText(lineReferenceMarkdown(demandId, lineId, line.lineNumber, line.description))
      .then(() => {
        showToast('Line item reference copied.');
      })
      .catch(() => {
        showToast('Could not copy the line item reference.', 'danger');
      });
  });
  delegate(root, 'click', '[data-action="lines-batch-remove"]', () => {
    if (selectedLineIds.value.length > 0) void removeLines(selectedLineIds.value);
  });
  // Decision actions from the row kebab / right-click menu (one line) and the batch
  // menu (all selected). approve/negotiate open the price dialog; the rest record now.
  // Reopen with Update (HS2-Z6MFSA): say what changed, then reopen the rejected demand.
  delegate(root, 'click', '[data-action="reopen-update-open"]', () => {
    reopenUpdateOpen.value = true;
  });
  delegate(root, 'click', '[data-action="reopen-update-cancel"]', () => {
    reopenUpdateOpen.value = false;
  });
  delegate(root, 'click', '[data-action="reopen-update-send"]', () => {
    const update = elValue(root.querySelector('[name="reopen-update"]')).trim();
    if (update === '') {
      root.querySelector<HTMLElement>('[name="reopen-update"]')?.focus();
      return;
    }
    void reopenWithUpdateClient(update);
  });
  delegateCapture(root, 'close', 'dialog.reopen-update-dialog', () => {
    reopenUpdateOpen.value = false;
  });
  // Mark Ordered / Edit Order (FR-5.18, HS2-HYDRJX).
  delegate(root, 'click', '[data-action="order-open"]', (_e, el) => {
    const lineId = el.getAttribute('data-line-id');
    if (lineId !== null && lineId !== '') orderDialog.value = { lineId };
  });
  delegate(root, 'click', '[data-action="order-cancel"]', () => {
    orderDialog.value = null;
  });
  delegate(root, 'click', '[data-action="order-save"]', () => {
    void saveOrderClient(root);
  });
  delegate(root, 'click', '[data-action="order-fail-open"]', () => {
    const dialog = orderDialog.value;
    if (dialog !== null) orderDialog.value = { lineId: dialog.lineId, failing: true };
  });
  delegate(root, 'click', '[data-action="order-fail"]', () => {
    void failOrderClient(root);
  });
  delegate(root, 'click', '[data-action="order-clear"]', () => {
    void clearOrderClient();
  });
  delegateCapture(root, 'close', 'dialog.order-dialog', () => {
    orderDialog.value = null;
  });
  // Record Receipt / review receipts (FR-5.19, HS2-6AJZPE).
  delegate(root, 'click', '[data-action="receipt-open"]', (_e, el) => {
    const lineId = el.getAttribute('data-line-id');
    if (lineId !== null && lineId !== '') receiptDialog.value = { lineId };
  });
  delegate(root, 'click', '[data-action="receipt-cancel"]', () => {
    receiptDialog.value = null;
  });
  delegate(root, 'click', '[data-action="receipt-save"]', () => {
    void saveReceiptClient(root);
  });
  delegate(root, 'click', '[data-action="receipt-delete"]', (_e, el) => {
    const receiptId = el.getAttribute('data-receipt-id');
    if (receiptId !== null && receiptId !== '') void deleteReceiptClient(receiptId);
  });
  delegate(root, 'click', '[data-action="receipt-reopen-short"]', () => {
    void reopenShortClient();
  });
  delegateCapture(root, 'close', 'dialog.receipt-dialog', () => {
    receiptDialog.value = null;
  });
  // Needs Clarification… (HS2-R5H22P): ask the question, then send it.
  delegate(root, 'click', '[data-action="line-clarify-open"]', (_e, el) => {
    const lineId = el.getAttribute('data-item-id');
    if (lineId !== null && lineId !== '') clarifyDialog.value = { lineId };
  });
  delegate(root, 'click', '[data-action="clarify-cancel"]', () => {
    clarifyDialog.value = null;
  });
  delegate(root, 'click', '[data-action="clarify-send"]', () => {
    const dialog = clarifyDialog.value;
    const question = elValue(root.querySelector('[name="clarify-question"]')).trim();
    if (dialog === null) return;
    if (question === '') {
      root.querySelector<HTMLElement>('[name="clarify-question"]')?.focus();
      return;
    }
    void requestLineClarificationClient(dialog.lineId, question);
  });
  delegateCapture(root, 'close', 'dialog.clarify-dialog', () => {
    clarifyDialog.value = null;
  });
  delegate(root, 'click', '[data-action="line-decide"]', (e, el) => {
    const id = el.getAttribute('data-item-id');
    const action = el.getAttribute('data-decide-action');
    if (id === null || action === null) return;
    const current = decisions.value.find((decision) => decision.lineItemId === id)?.action;
    if (current === action) {
      void clearLineDecision(id);
      return;
    }
    if (DECISION_NEEDS_PRICE.has(action)) {
      decisionVendor.value = null;
      decisionReason.value = '';
      lineDecisionDialog.value = { lineIds: [id], action };
    } else void recordLineDecisions([id], action, null, null);
  });
  // A quote chip is a direct approval shortcut. Lowest-price quotes can be
  // accepted immediately; choosing a non-lowest quote opens the existing dialog
  // with that vendor and price preselected so a reason is captured.
  delegate(root, 'click', '[data-action="line-quote-approve"]', (e, el) => {
    const lineId = el.getAttribute('data-line-id');
    const quoteLineId = el.getAttribute('data-quote-line-id');
    const price = el.getAttribute('data-quote-price');
    if (lineId === null || quoteLineId === null || price === null) return;
    if (el.getAttribute('data-lowest') === 'true') {
      void recordLineDecisions([lineId], 'approve', price, null, quoteLineId);
      return;
    }
    decisionVendor.value = quoteLineId;
    decisionReason.value = '';
    lineDecisionDialog.value = { lineIds: [lineId], action: 'approve' };
  });
  delegate(root, 'click', '[data-action="batch-decide"]', (e, el) => {
    const action = el.getAttribute('data-decide-action');
    const ids = selectedLineIds.value;
    if (action === null || ids.length === 0) return;
    if (DECISION_NEEDS_PRICE.has(action) && ids.length > 1) {
      const pricedAction = action === 'approve' ? 'approve' : 'negotiate';
      const recommendations = recommendedBatchDecisions(pricedAction, ids, quoteMatrix.value, analysisReport.value);
      if (recommendations !== undefined)
        void recordLineDecisions(ids, action, null, null, undefined, undefined, recommendations);
    } else if (DECISION_NEEDS_PRICE.has(action)) {
      decisionVendor.value = null;
      decisionReason.value = '';
      lineDecisionDialog.value = { lineIds: ids, action };
    } else void recordLineDecisions(ids, action, null, null);
  });
  // The price/note dialog for approve/negotiate.
  delegate(root, 'click', '[data-action="decision-dialog-cancel"]', () => {
    lineDecisionDialog.value = null;
  });
  // Track the chosen quote + reason in signals so the controlled selects survive
  // re-renders; a non-best choice reveals the reason control (HS2-EKEE9S).
  delegate(root, 'change', '[name="line-decide-vendor"]', (e) => {
    decisionVendor.value = selectValue(e.target as Element);
  });
  delegate(root, 'change', '[name="line-decide-reason"]', (e) => {
    decisionReason.value = selectValue(e.target as Element);
  });
  delegate(root, 'click', '[data-action="decision-dialog-save"]', () => {
    const d = lineDecisionDialog.value;
    if (d === null) return;
    const price = elValue(root.querySelector('[name="line-decide-price"]'));
    const note = elValue(root.querySelector('[name="line-decide-note"]'));
    // The chosen quote is the reviewer's pick (decisionVendor signal — the source
    // of truth, immune to any morph reset of the select) or the default best.
    const best = elValue(root.querySelector('[name="line-decide-best-quote"]'));
    const vendor = decisionVendor.value ?? best;
    // When a non-best quote is chosen, capture the reason (a common label or the
    // "Other" free text). Guard the save if a reason is required but missing.
    let selectionReason: string | undefined;
    if (vendor !== '' && best !== '' && vendor !== best) {
      const picked = decisionReason.value;
      selectionReason =
        picked === 'Other' ? elValue(root.querySelector('[name="line-decide-reason-other"]')).trim() : picked;
      if (selectionReason === '') {
        notice.value = 'Choose a reason for not selecting the lowest quote.';
        return;
      }
    }
    void recordLineDecisions(
      d.lineIds,
      d.action,
      price === '' ? null : price,
      note === '' ? null : note,
      vendor === '' ? undefined : vendor,
      selectionReason,
    );
  });
  delegateCapture(root, 'close', 'dialog.decision-dialog', () => {
    lineDecisionDialog.value = null;
    decisionVendor.value = null;
    decisionReason.value = '';
  });
  delegate(root, 'click', '[data-action="line-editor-cancel"]', () => {
    lineEditorDialog.value = null;
  });
  delegate(root, 'input', '[name="line-editor-hs-code"], [name="line-editor-hs-duty"]', (_e, el) => {
    el.closest('.demand-line-item-editor-dialog')?.setAttribute('data-hs-code-edited', '');
  });
  delegate(root, 'click', 'details[data-action="line-editor-advanced-toggle"] summary', (event, el) => {
    const dialog = lineEditorDialog.value;
    if (dialog === null) return;
    const details = el.closest('details');
    if (details === null) return;
    // Record the intended state before intake can replace the editor. The
    // browser's deferred toggle event can otherwise report the old element.
    event.preventDefault();
    details.open = !details.open;
    lineEditorDialog.value = { ...dialog, advancedOpen: details.open };
  });
  delegate(root, 'click', '[data-action="line-editor-save"]', () => {
    const dialog = lineEditorDialog.value;
    if (dialog === null || demandReadOnly()) return;
    const editor = root.querySelector('.demand-line-item-editor-dialog');
    void saveLineItemEditor(dialog.lineId, {
      description: elValue(root.querySelector('[name="line-editor-description"]')),
      quantity: elValue(root.querySelector('[name="line-editor-quantity"]')),
      unit: elValue(root.querySelector('[name="line-editor-unit"]')),
      hsCode: elValue(root.querySelector('[name="line-editor-hs-code"]')),
      hsCodeDutyPct: elValue(root.querySelector('[name="line-editor-hs-duty"]')),
      hsCodeEdited: editor?.hasAttribute('data-hs-code-edited') ?? false,
    });
  });
  delegateCapture(root, 'close', 'dialog.demand-line-item-editor-dialog', () => {
    lineEditorDialog.value = null;
  });
  delegate(root, 'click', '[data-action="line-evidence-open"]', (_e, el) => {
    const lineId = el.getAttribute('data-line-id');
    if (lineId !== null) {
      lineEvidenceMessage.value = '';
      lineEvidenceView.value = 'pricing';
      lineEvidenceDetailActive.value = false;
      lineBenchmarkDialogOpen.value = false;
      lineEvidenceDialog.value = { lineId };
    }
  });
  delegate(root, 'click', '[data-action="evidence-close"]', () => {
    lineEvidenceDialog.value = null;
    lineBenchmarkDialogOpen.value = false;
  });
  delegateCapture(root, 'close', 'dialog.demand-line-item-evidence-dialog', () => {
    lineEvidenceDialog.value = null;
    lineBenchmarkDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="evidence-view"]', (_e, el) => {
    const next = el.getAttribute('data-item-id');
    if (next !== 'pricing' && next !== 'quotes' && next !== 'benchmarks') return;
    lineEvidenceView.value = next;
    // On compact devices choosing a view pushes it over the list (HS2-AW53XQ).
    lineEvidenceDetailActive.value = true;
  });
  delegate(root, 'click', '[data-action="evidence-benchmark-open"]', () => {
    lineEvidenceMessage.value = '';
    lineBenchmarkDialogOpen.value = true;
  });
  delegate(root, 'click', '[data-action="evidence-benchmark-close"]', () => {
    lineBenchmarkDialogOpen.value = false;
  });
  delegateCapture(root, 'close', 'dialog.evidence-benchmark-dialog', () => {
    lineBenchmarkDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="confirm-hs-code"]', (_e, el) => {
    const lineId = el.getAttribute('data-line-id');
    if (lineId !== null && lineId !== '') void confirmHsCodeClient(lineId);
  });
  delegate(root, 'click', '[data-action="evidence-add-benchmark"]', () => {
    void addManualBenchmarkClient(root);
  });
  // Right-clicking a line opens the selection actions at the pointer. An
  // unselected target becomes the sole selection; right-clicking inside an
  // existing multi-selection preserves the batch (HS2-5HGDNP/HS2-E52Y1K).
  delegate(root, 'contextmenu', '[data-line-item]', (e, el) => {
    if (demandReadOnly()) return; // no row actions on a cancelled demand (HS2-ZXQAM6)
    const lineId = el.getAttribute('data-line-item');
    if (lineId === null) return;
    if (!selectedLineIds.value.includes(lineId)) {
      selectedLineIds.value = [lineId];
      lineSelectionAnchor.value = lineId;
    }
    showContextMenu({ kind: 'selection' }, e);
  });

  const supportingDocuments = (): IntakeDoc[] =>
    Object.values((workspace.value?.filesByKind ?? {}) as Record<string, IntakeDoc[]>)
      .flat()
      .filter((document) => document.extractionStatus === undefined || document.extractionStatus === 'done');
  const applyDocumentSelection = (documentId: string, event: MouseEvent | KeyboardEvent): void => {
    const result = selectLine({
      orderedIds: supportingDocuments().map((document) => document.id),
      selectedIds: selectedDocumentIds.value,
      clickedId: documentId,
      anchorId: documentSelectionAnchor.value,
      toggle: event.metaKey || event.ctrlKey,
      range: event.shiftKey,
    });
    selectedDocumentIds.value = result.selectedIds;
    documentSelectionAnchor.value = result.anchorId;
  };
  const documentFor = (documentId: string): IntakeDoc | undefined =>
    supportingDocuments().find((document) => document.id === documentId);
  const openSelectedDocument = (documentId: string): void => {
    void openDocumentClient(documentId);
  };
  delegate(root, 'click', '[data-document-id]', (e, el) => {
    if (isNestedControl(e.target, el) || !isContentItemActivation(e, el as HTMLElement)) return;
    const documentId = el.getAttribute('data-document-id');
    if (documentId !== null) applyDocumentSelection(documentId, e as MouseEvent);
  });
  delegate(root, 'dblclick', '[data-document-id]', (e, el) => {
    if (isNestedControl(e.target, el)) return;
    const documentId = el.getAttribute('data-document-id');
    if (documentId !== null) {
      e.preventDefault();
      openSelectedDocument(documentId);
    }
  });
  // Capture phase: the tile is an interactive Kerf ContentItem, and this must run
  // before wireContentItems (which skips handled keys) so Enter opens while Space
  // selects with Shift/Cmd extension (HS2-J5C8SH).
  delegateCapture(root, 'keydown', '[data-document-id]', (e, el) => {
    // Enter/Space on a nested control (the "Add as quote" button) belong to that
    // control, not the tile (HS2-GH2HK4).
    if (isNestedControl(e.target, el)) return;
    const key = activationKey(e as KeyboardEvent);
    const documentId = el.getAttribute('data-document-id');
    if (documentId === null || key === null) return;
    e.preventDefault();
    if (key === 'enter') openSelectedDocument(documentId);
    else applyDocumentSelection(documentId, e as KeyboardEvent);
  });
  delegate(root, 'contextmenu', '[data-document-id]', (e, el) => {
    const documentId = el.getAttribute('data-document-id');
    if (documentId === null || demandReadOnly()) return;
    if (!selectedDocumentIds.value.includes(documentId)) {
      selectedDocumentIds.value = [documentId];
      documentSelectionAnchor.value = documentId;
    }
    showContextMenu({ kind: 'selection' }, e);
  });
  delegate(root, 'click', '[data-action="documents-clear-selection"]', () => {
    selectedDocumentIds.value = [];
    documentSelectionAnchor.value = '';
  });
  delegate(root, 'click', '[data-action="documents-download"]', () => {
    for (const documentId of selectedDocumentIds.value) void openDocumentClient(documentId, true);
  });
  delegate(root, 'click', '[data-action="document-download"]', (_e, el) => {
    const documentId = el.getAttribute('data-item-id');
    if (documentId !== null) {
      if (documentPreview.value?.source === 'library') void openLibraryDocumentClient(documentId, true);
      else void openDocumentClient(documentId, true);
    }
  });
  delegate(root, 'click', '[data-action="document-preview"]', () => {
    const documentId = selectedDocumentIds.value[0];
    if (
      documentId !== undefined &&
      selectedDocumentIds.value.length === 1 &&
      isImageDocument(documentFor(documentId) ?? { filename: '' })
    )
      void openDocumentClient(documentId);
  });
  delegate(root, 'click', '[data-action="documents-copy-reference"]', () => {
    const demandId = currentDemandId();
    if (demandId === undefined) return;
    const references = selectedDocumentIds.value.flatMap((documentId) => {
      const document = documentFor(documentId);
      return document === undefined ? [] : [documentReferenceMarkdown(demandId, document.id, document.filename)];
    });
    if (references.length === 0) return;
    void navigator.clipboard
      .writeText(references.join('\n'))
      .then(() => {
        showToast(references.length === 1 ? 'File reference copied.' : 'File references copied.');
      })
      .catch(() => {
        showToast('Could not copy the file reference.', 'danger');
      });
  });
  delegate(root, 'click', '[data-action="document-replace-open"]', () => {
    const documentId = selectedDocumentIds.value[0];
    const input = root.querySelector<HTMLInputElement>('[data-document-replace-input]');
    if (documentId === undefined || selectedDocumentIds.value.length !== 1 || input === null) return;
    input.dataset.documentReplaceId = documentId;
    input.click();
  });
  delegate(root, 'change', '[data-document-replace-input]', (e, el) => {
    const input = el as HTMLInputElement;
    const documentId = input.dataset.documentReplaceId;
    const file = input.files?.[0];
    input.value = '';
    if (documentId !== undefined && file !== undefined) void replaceDocumentClient(documentId, file);
  });
  delegate(root, 'click', '[data-action="documents-delete"]', () => {
    if (selectedDocumentIds.value.length > 0) void deleteDocumentsClient([...selectedDocumentIds.value]);
  });
  delegate(root, 'click', '[data-action="document-promote"]', (_e, el) => {
    const documentId = selectedDocumentIds.value[0];
    if (documentId !== undefined && selectedDocumentIds.value.length === 1)
      void promoteDocumentClient(documentId, el.getAttribute('data-project-id') ?? undefined);
  });
  delegate(root, 'click', '[data-action="documents-change-type"]', (_e, el) => {
    const kind = el.getAttribute('data-item-id');
    if (selectedDocumentIds.value.length > 0 && (kind === 'demand' || kind === 'quotation' || kind === 'supporting'))
      void moveDocumentsClient([...selectedDocumentIds.value], kind);
  });
  delegate(root, 'click', '[data-action="document-preview-close"]', () => {
    documentPreview.value = null;
  });
  delegate(root, 'click', '[data-action="document-preview-annotate"]', () => {
    const active = documentPreview.value;
    if (active !== null)
      documentPreview.value = { ...active, annotating: active.annotating !== true, point: undefined };
  });
  delegate(root, 'click', '[data-document-annotation-image]', (event, el) => {
    const active = documentPreview.value;
    if (active === null || active.annotating !== true || demandReadOnly()) return;
    const bounds = el.getBoundingClientRect();
    const pointer = event as MouseEvent;
    documentPreview.value = {
      ...active,
      point: {
        x: Math.max(0, Math.min(1, (pointer.clientX - bounds.left) / bounds.width)),
        y: Math.max(0, Math.min(1, (pointer.clientY - bounds.top) / bounds.height)),
      },
    };
  });
  delegate(root, 'keydown', '[data-document-annotation-image]', (event) => {
    const keyboard = event as KeyboardEvent;
    const active = documentPreview.value;
    if ((keyboard.key !== 'Enter' && keyboard.key !== ' ') || active?.annotating !== true || demandReadOnly()) return;
    keyboard.preventDefault();
    documentPreview.value = { ...active, point: { x: 0.5, y: 0.5 } };
  });
  delegate(root, 'submit', '[data-document-annotation-form]', (event, el) => {
    event.preventDefault();
    const input = el.querySelector('[name="body"]');
    const body = input === null ? undefined : elValue(input).trim();
    const point = documentPreview.value?.point;
    if (body !== undefined && body !== '' && point !== undefined && !demandReadOnly())
      void addDocumentAnnotationClient(body, point);
  });
  delegate(root, 'click', '[data-action="document-annotation-delete"]', (_event, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null && !demandReadOnly()) void deleteDocumentAnnotationClient(id);
  });
  delegate(root, 'click', '[data-action="gallery-copy-reference"]', () => {
    const documentId = documentPreview.value?.documentId;
    const demandId = currentDemandId();
    const document = documentId === undefined ? undefined : documentFor(documentId);
    if (demandId === undefined || document === undefined) return;
    void navigator.clipboard
      .writeText(documentReferenceMarkdown(demandId, document.id, document.filename))
      .then(() => {
        showToast('File reference copied.');
      })
      .catch(() => {
        showToast('Could not copy the file reference.', 'danger');
      });
  });
  delegate(root, 'click', '[data-action="gallery-replace-open"]', () => {
    if (!demandReadOnly()) root.querySelector<HTMLInputElement>('[data-gallery-replace-input]')?.click();
  });
  delegate(root, 'change', '[data-gallery-replace-input]', (_event, el) => {
    const input = el as HTMLInputElement;
    const file = input.files?.[0];
    const documentId = documentPreview.value?.documentId;
    input.value = '';
    if (file !== undefined && documentId !== undefined && !demandReadOnly())
      void replaceDocumentClient(documentId, file);
  });
  delegate(root, 'click', '[data-action="gallery-delete"]', () => {
    const documentId = documentPreview.value?.documentId;
    if (documentId !== undefined && !demandReadOnly()) void deleteDocumentsClient([documentId]);
  });
  delegateCapture(root, 'close', 'dialog.document-preview-dialog', () => {
    documentPreview.value = null;
  });
  delegate(root, 'click', '[data-action="document-preview-zoom"]', (_e, el) => {
    const active = documentPreview.value;
    if (active === null) return;
    const direction = Number(el.getAttribute('data-item-id'));
    if (direction !== -1 && direction !== 1) return;
    documentPreview.value = { ...active, zoom: shiftDocumentGalleryZoom(active.zoom ?? 1, direction) };
  });
  const shiftDocumentPreview = (delta: number): void => {
    const active = documentPreview.value;
    if (active === null) return;
    const images = (active.source === 'library' ? libraryDocs.value : supportingDocuments()).filter(isImageDocument);
    const index = images.findIndex((document) => document.id === active.documentId);
    if (index < 0 || !Number.isFinite(delta) || images.length < 2) return;
    const next = images[(index + delta + images.length) % images.length];
    if (next !== undefined) {
      if (active.source === 'library') void openLibraryDocumentClient(next.id);
      else void openDocumentClient(next.id);
    }
  };
  delegate(root, 'click', '[data-action="document-preview-shift"]', (_e, el) => {
    shiftDocumentPreview(Number(el.getAttribute('data-item-id')));
  });
  // Left/Right arrows step through the gallery like its previous/next buttons (HS2-2NB74E).
  delegate(root, 'keydown', '.document-preview-dialog', (e) => {
    const event = e as KeyboardEvent;
    const step = galleryArrowStep(event);
    if (step === null || documentPreview.value === null) return;
    event.preventDefault();
    shiftDocumentPreview(step);
  });
  // Header fields auto-save on blur — no Save button (HS2-1ZGN3H).
  delegate(root, 'blur', '[data-intake-field]', (e) => {
    const el = (e.target as Element).closest('[data-intake-field]');
    const name = el?.getAttribute('data-intake-field');
    if (el !== null && name !== null && name !== undefined) void saveIntakeField(name, elValue(el));
  });
  delegate(root, 'click', '[data-action="intake-confirm"]', () => {
    void confirmIntake();
  });
  delegate(root, 'click', '[data-action="demand-return-open"]', () => {
    returnDialogOpen.value = true;
  });
  delegate(root, 'click', '[data-action="demand-return-close"]', () => {
    returnDialogOpen.value = false;
  });
  delegateCapture(root, 'close', 'dialog.return-demand-dialog', () => {
    returnDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="demand-return-submit"]', () => {
    void (async () => {
      const orgId = me.value?.organizationId;
      const demandId = currentDemandId();
      if (orgId == null || demandId === undefined) return;
      const note = elValue(root.querySelector('[name="return-note"]')).trim();
      const labels = elValue(root.querySelector('[name="return-questions"]'))
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      if (labels.length > 8 || (note === '' && labels.length === 0)) {
        notice.value = 'Add a note or up to eight questions.';
        return;
      }
      const body =
        labels.length === 0
          ? { note }
          : {
              note: note || undefined,
              form: {
                title: 'Questions About Your Demand',
                fields: labels.map((label, index) => ({
                  key: `question_${String(index + 1)}`,
                  label,
                  type: 'textarea' as const,
                  required: true,
                })),
              },
            };
      const result = await api.returnDemand(orgId, demandId, body);
      if (!result.ok) {
        notice.value = result.error ?? 'Could not return demand.';
        return;
      }
      returnDialogOpen.value = false;
      await Promise.all([reopenDemandIfShown(demandId), loadDemands()]);
    })();
  });
  delegate(root, 'click', '[data-action="demand-resubmit"]', () => {
    void (async () => {
      const orgId = me.value?.organizationId;
      const demandId = currentDemandId();
      if (orgId == null || demandId === undefined) return;
      const result = await api.resubmitDemand(orgId, demandId);
      if (!result.ok) {
        notice.value = result.error ?? 'Could not resubmit demand.';
        return;
      }
      await Promise.all([reopenDemandIfShown(demandId), loadDemands()]);
    })();
  });

  delegate(root, 'click', '[data-demand-id]', (e) => {
    const rowEl = (e.target as Element).closest('[data-demand-id]');
    const id = rowEl?.getAttribute('data-demand-id');
    if (id !== null && id !== undefined) void openDemand(id);
  });

  delegate(root, 'click', '[data-action="editor-seg"]', (e) => {
    const seg = (e.target as Element).closest<HTMLElement>('[data-action="editor-seg"]')?.dataset.segmentValue;
    if (
      seg === 'info' ||
      seg === 'lines' ||
      seg === 'quotes' ||
      seg === 'files' ||
      seg === 'summary' ||
      seg === 'decision-summary'
    ) {
      if (!canOpenDemandSegment(seg)) return;
      // A pending link restore would reapply its own section when it finishes,
      // reverting this pick on a slow load (HS2-2D8AST); the user's choice wins.
      cancelAppHistoryRestore();
      cancelMultifileBatch();
      editorSegment.value = seg;
      if (seg === 'quotes') void loadQuoteMatrix();
    }
  });
  const revealLine = (lineId: string, attemptsLeft = 30): void => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const line = [...root.querySelectorAll<HTMLElement>('[data-line-item]')].find(
          (candidate) => candidate.dataset.lineItem === lineId,
        );
        // Switching to the Line items segment renders asynchronously (more slowly under load),
        // so keep looking for the row for a while before giving up. A large list may not have
        // built the row yet, so render them all (HS2-HVR0HY).
        if (line === undefined) {
          showAllLineRows();
          if (attemptsLeft > 0) revealLine(lineId, attemptsLeft - 1);
          return;
        }
        // Kerf's focus ring on the multi-select row marks the referenced line, and
        // keyboard users land on it (HS2-TYTAS6; replaces the app background flash).
        line.scrollIntoView({ behavior: 'smooth', block: 'center' });
        line.focus({ preventScroll: true });
      }),
    );
  };
  delegate(root, 'click', '[data-line-reference]', (e, el) => {
    e.preventDefault();
    const demandId = el.getAttribute('data-demand-reference');
    const lineId = el.getAttribute('data-line-reference');
    if (demandId === null || lineId === null) return;
    void (async () => {
      if (currentDemandId() !== demandId) await openDemand(demandId);
      cancelMultifileBatch();
      editorSegment.value = 'lines';
      revealLine(lineId);
    })();
  });
  delegate(root, 'click', '[data-document-reference]', (e, el) => {
    e.preventDefault();
    const demandId = el.getAttribute('data-demand-reference');
    const documentId = el.getAttribute('data-document-reference');
    if (demandId === null || documentId === null) return;
    void (async () => {
      if (currentDemandId() !== demandId) await openDemand(demandId);
      cancelMultifileBatch();
      editorSegment.value = 'files';
      await openDocumentClient(documentId);
    })();
  });
  // Jump from an outcomes "undecided" link to that line on the Line items screen
  // (HS2-EQ0FVN); scroll-into-view + highlight is shared with internal references.
  delegate(root, 'click', '[data-goto-line]', (e) => {
    e.preventDefault();
    const lineId = (e.target as Element).closest<HTMLElement>('[data-goto-line]')?.dataset.gotoLine;
    if (lineId === undefined || lineId === '') return;
    cancelMultifileBatch();
    editorSegment.value = 'lines';
    revealLine(lineId);
  });
  // The Summary / Outcomes in-content pane switch (HS2-D2W8M2).
  delegate(root, 'click', '[data-action="summary-pane"]', (e) => {
    const pane = (e.target as Element).closest<HTMLElement>('[data-action="summary-pane"]')?.dataset.segmentValue;
    if (pane === 'overview' || pane === 'decision' || pane === 'outcomes') summaryPane.value = pane;
  });
  delegate(root, 'click', '[data-action="retry-quotes"]', () => {
    void loadQuoteMatrix();
  });
  delegate(root, 'click', '[data-action="retry-ai-usage"]', () => {
    void loadAiUsage();
  });
  // A click on a link inside a rendered Markdown preview follows the link and never
  // starts editing (HS2-61J7B5). With root-delegated handlers the preview's own
  // handler is where that click must stop; this covers pointer clicks and the
  // click a focused link's Enter produces. Kerf's wireContentItems turns Enter/Space
  // on the preview itself into a click, so the keyboard edits the same way.
  const clickedLinkIn = (e: Event, preview: Element): boolean => isNestedControl(e.target, preview);
  // Since Kerf beta 106, wireContentItems also activates on Cmd/Ctrl+Enter, with a
  // click that carries the modifier (KF-45RNDS). A modified Enter on a preview stays
  // a no-op: ignore a keyboard click (detail 0) that carries Cmd or Ctrl.
  const modifiedKeyActivation = (e: Event): boolean =>
    e instanceof MouseEvent && e.detail === 0 && (e.ctrlKey || e.metaKey);
  // Append-only notes (HS2-E33XQD): Add Note appends from the composer in the same list; the
  // author's Edit opens their newest note within its minute; Save/Cancel close it.
  delegate(root, 'click', '[data-action="note-add"]', (_e, el) => {
    const scope = el.getAttribute('data-note-line') ?? '';
    const list = el.closest('.demand-notes');
    const input = list?.querySelector<HTMLElement>('[name="note-compose"]') ?? null;
    const body = elValue(input).trim();
    if (body === '') return;
    void addNoteClient(body, scope === '' ? null : scope).then((ok) => {
      if (ok && input !== null) (input as HTMLElement & { value: string }).value = '';
    });
  });
  delegate(root, 'click', '[data-action="note-edit"]', (_e, el) => {
    editingNoteId.value = el.getAttribute('data-note-id');
    requestAnimationFrame(() => {
      root.querySelector<HTMLElement>('[name="note-edit-body"]')?.focus();
    });
  });
  delegate(root, 'click', '[data-action="note-edit-cancel"]', () => {
    editingNoteId.value = null;
  });
  delegate(root, 'click', '[data-action="note-edit-save"]', (_e, el) => {
    const id = el.getAttribute('data-note-id');
    const body = elValue(el.closest('.demand-note')?.querySelector('[name="note-edit-body"]') ?? null).trim();
    if (id !== null && body !== '') void saveNoteEditClient(id, body);
  });
  // The unread-notes banner's Review opens Documents, where the notes are (HS2-E33XQD).
  delegate(root, 'click', '[data-action="notes-review"]', () => {
    cancelMultifileBatch();
    editorSegment.value = 'files';
  });
  // Showing Documents reads the demand's notes: whenever it is the active segment and notes by
  // others arrived since this member's last read, mark them read (HS2-E33XQD).
  // Opening a line's dialog reads that line's notes the same way (HS2-JRQT8A).
  let lineNotesReadPending = '';
  effect(() => {
    const lineId = lineEditorDialog.value?.lineId ?? null;
    const ws = workspace.value;
    if (lineId === null || ws === null) return;
    const list = notesInScope((ws.notes ?? []) as DemandNote[], lineId);
    const unread = unreadNotes(list, ((ws.noteReads ?? {}) as Record<string, string>)[lineId], me.value?.memberId);
    if (unread.length === 0) return;
    const key = `${lineId}:${unread.at(-1)?.id ?? ''}`;
    if (key === lineNotesReadPending) return;
    lineNotesReadPending = key;
    void markNotesReadClient(lineId);
  });
  let notesReadPending = '';
  effect(() => {
    const segment = editorSegment.value;
    const unread = unreadDemandNotes();
    const demandId = currentDemandId();
    if (segment !== 'files' || unread.length === 0 || demandId === undefined) return;
    const key = `${demandId}:${unread.at(-1)?.id ?? ''}`;
    if (key === notesReadPending) return;
    notesReadPending = key;
    void markNotesReadClient(null);
  });

  // Demand description (Markdown): a single click edits, empty or filled (HS2-61J7B5);
  // editing auto-focuses the textarea (HS2-67W2MT, HS2-GR7NCQ).
  const startDescEdit = (): void => {
    descEditing.value = true;
    requestAnimationFrame(() => {
      root.querySelector<HTMLElement>('.editor-purpose')?.focus();
    });
  };
  delegate(root, 'click', '[data-action="edit-desc-empty"]', () => {
    startDescEdit();
  });
  delegate(root, 'click', '[data-action="edit-desc"]', (e, el) => {
    if (!clickedLinkIn(e, el) && !modifiedKeyActivation(e)) startDescEdit();
  });
  delegate(root, 'blur', '[data-action="desc-input"]', (e) => {
    descEditing.value = false;
    void saveIntakeField('purpose', elValue(e.target as Element));
  });
  // Needed by (date): click the formatted preview to edit; blur returns to the
  // preview (the value is auto-saved by the [data-intake-field] blur handler
  // above). HS2-8TG0P1.
  delegate(root, 'click', '[data-action="edit-neededby"]', () => {
    neededByEditing.value = true;
    requestAnimationFrame(() => {
      root.querySelector<HTMLElement>('input[name="intake-neededBy"]')?.focus();
    });
  });
  delegate(root, 'blur', '[data-action="neededby-input"]', () => {
    neededByEditing.value = false;
  });

  // Info's Assigned To (FR-5.20, HS2-C63T9N): saved on change, against the assignee the editor shows.
  delegate(root, 'change', '[name="demand-assignee"]', (_e, el) => {
    const demand = workspace.value?.demand as { id: string; assigneeMemberId?: string | null } | undefined;
    if (demand === undefined) return;
    const value = (el as HTMLElement & { value: string }).value;
    const next = value === '' ? null : value;
    const current = demand.assigneeMemberId ?? null;
    if (next !== current) void assignDemandClient(demand.id, next, current);
  });
  delegate(root, 'change', '[data-action="demand-status"]', (e) => {
    demandStatusFilter.value = (e.target as HTMLSelectElement).value;
    void loadDemands();
  });
  delegate(root, 'change', '[data-action="demand-site"]', (e) => {
    // Site is filtered client-side from the loaded list (by resolved site name).
    demandSiteFilter.value = (e.target as HTMLSelectElement).value;
  });
  delegate(root, 'click', '[data-action="lib-tab"]', (e) => {
    const v = (e.target as Element).closest<HTMLElement>('[data-action="lib-tab"]')?.dataset.segmentValue;
    if (v === 'documents' || v === 'facts') libraryTab.value = v;
  });
  delegate(root, 'click', '[data-action="savings-period-nav"]', (e) => {
    const v = (e.target as Element).closest<HTMLElement>('[data-action="savings-period-nav"]')?.dataset.itemId;
    if (v === 'all' || v === '90d' || v === '30d') {
      savingsPeriod.value = v;
      void loadOrgSavings();
    }
  });
  // The Savings site filter is a wa-select; read the host .value (not the shadow
  // input, which returns the display label) — the change target here is the host.
  delegate(root, 'change', '[name="savings-site"]', (_e, el) => {
    savingsSite.value = (el as HTMLElement & { value: string }).value;
    void loadOrgSavings();
  });
  delegate(root, 'click', '[data-action="export-savings"]', (e) => {
    const format = (e.target as Element).closest<HTMLElement>('[data-action="export-savings"]')?.dataset.itemId;
    const orgId = me.value?.organizationId;
    if (orgId === undefined || orgId === null || (format !== 'pdf' && format !== 'xlsx')) return;
    const link = document.createElement('a');
    link.href = savingsExportHref(orgId, format);
    document.body.append(link);
    link.click();
    link.remove();
  });
  delegate(root, 'input', '[data-action="demand-search"]', (e) => {
    demandSearch.value = (e.target as HTMLInputElement).value;
  });

  // --- Quotes (Flow F3) ---
  delegate(root, 'change', '[data-quote-upload]', (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>('[data-quote-upload]');
    if (input === null) return;
    const kind = input.getAttribute('data-quote-upload') ?? 'quotation';
    const files = input.files;
    if (files === null || files.length === 0) return;
    void uploadQuoteFiles(Array.from(files), kind);
  });
  delegate(root, 'click', '[data-action="quote-add-open"]', () => {
    quoteTaxBasis.value = 'unknown';
    quoteDialog.value = { mode: 'add' };
  });
  delegate(root, 'change', 'kui-select[name="q-tax-basis"]', (e) => {
    const value = selectValue(e.target as Element);
    if (value === 'unknown' || value === 'included' || value === 'excluded') quoteTaxBasis.value = value;
  });
  // The vendor card is a Kerf interactive ContentItem (HS2-CY4F20): a pointer
  // click or wireContentItems' Enter/Space activation lands here.
  delegate(root, 'click', '[data-action="quote-view"]', (_e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null && id !== '') quoteDialog.value = { mode: 'view', quotationId: id };
  });
  delegate(root, 'click', '[data-action="quote-edit"]', (e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null && id !== '') quoteDialog.value = { mode: 'edit', quotationId: id };
  });
  delegate(root, 'click', '[data-action="open-questions"]', () => {
    void openQuestionsDialogClient();
  });
  delegate(root, 'click', '[data-action="questions-dialog-close"]', () => {
    questionsDialogOpen.value = false;
  });
  // Cancel discards in-progress edits (uncontrolled fields drop when the dialog
  // unmounts); Save draft persists them per-member (HS2-DVY1SE).
  delegate(root, 'click', '[data-action="questions-dialog-cancel"]', () => {
    questionsDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-form-save-draft]', (e) => {
    const id = (e.target as Element).closest('[data-form-save-draft]')?.getAttribute('data-form-save-draft');
    if (id !== null && id !== undefined) void saveQuestionsDraftClient(root, id);
  });
  delegateCapture(root, 'close', 'dialog.questions-dialog', () => {
    questionsDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="quote-dialog-cancel"]', () => {
    quoteDialog.value = null;
  });
  delegate(root, 'click', '[data-action="quote-dialog-save"]', () => {
    void saveQuoteDialog(root);
  });
  delegateCapture(root, 'close', 'dialog.quote-view-dialog', () => {
    quoteDialog.value = null;
  });
  delegateCapture(root, 'close', 'dialog.quote-dialog', () => {
    quoteDialog.value = null;
  });
  delegate(root, 'change', '.basis-select', (e) => {
    const el = e.target as HTMLSelectElement;
    const name = el.getAttribute('name') ?? '';
    const quotationId = name.startsWith('quote-basis:') ? name.slice('quote-basis:'.length) : '';
    if (quotationId !== '') void setQuoteBasis(quotationId, el.value);
  });
  delegate(root, 'change', '[name^="quote-map:"]', (e) => {
    const el = e.target as HTMLSelectElement;
    const name = el.getAttribute('name') ?? '';
    const quoteLineId = name.startsWith('quote-map:') ? name.slice('quote-map:'.length) : '';
    if (quoteLineId !== '' && el.value !== '') void remapQuoteLineClient(quoteLineId, el.value);
  });

  // --- Price-matrix cell selection and unmapping (HS2-QV3276) ---
  // An editable matrix is a role="grid" whose mapped cells and row/column headers
  // carry `data-matrix-pos`. Selection rules live in lib/matrix-selection.ts.
  const MATRIX_POSITION = 'table.matrix[role="grid"] [data-matrix-pos]';
  const currentMatrixGrid = (): MatrixGrid | null =>
    quoteMatrix.value === null || demandReadOnly() ? null : matrixGrid(quoteMatrix.value);
  const matrixModifiers = (event: MouseEvent | KeyboardEvent): SelectionModifiers => ({
    toggle: event.metaKey || event.ctrlKey,
    range: event.shiftKey,
  });
  const selectMatrixAt = (position: MatrixPosition, modifiers: SelectionModifiers): void => {
    const grid = currentMatrixGrid();
    const target = targetAt(position);
    if (grid === null || target === null) return;
    const current = reconcileMatrixSelection(grid, matrixSelection.value);
    matrixSelection.value = selectMatrixTarget(grid, current, target, modifiers);
    matrixFocus.value = position;
  };
  /** Right-click or the menu key acts on the current selection when it already covers
   *  the target; otherwise the target becomes the selection, as with line items. */
  const selectMatrixTargetForMenu = (position: MatrixPosition): void => {
    const grid = currentMatrixGrid();
    const target = targetAt(position);
    if (grid === null || target === null) return;
    const current = reconcileMatrixSelection(grid, matrixSelection.value);
    const ids = selectMatrixTarget(grid, EMPTY_MATRIX_SELECTION, target, { toggle: false, range: false }).selected;
    const covered = ids.length > 0 && ids.every((id) => current.selected.includes(id));
    matrixSelection.value = covered
      ? current
      : selectMatrixTarget(grid, current, target, { toggle: false, range: false });
    matrixFocus.value = position;
  };
  // Focus moves synchronously: every position is already rendered (only its tabindex
  // changes), so a fast run of arrow presses never acts on a stale cell.
  const focusMatrixPosition = (position: MatrixPosition): void => {
    matrixFocus.value = position;
    root
      .querySelector<HTMLElement>(`table.matrix[role="grid"] [data-matrix-pos="${matrixPositionKey(position)}"]`)
      ?.focus();
  };
  const unmapMatrixSelection = (): void => {
    const grid = currentMatrixGrid();
    if (grid === null) return;
    const { selected, anchor } = reconcileMatrixSelection(grid, matrixSelection.value);
    if (selected.length === 0) return;
    matrixSelection.value = { selected: [], anchor };
    void unmapQuoteLinesClient(selected);
  };
  // Shift-click extends the selection; keep it from also selecting the cells' text.
  delegate(root, 'mousedown', MATRIX_POSITION, (e, el) => {
    if ((e as MouseEvent).shiftKey && !isNestedControl(e.target, el)) e.preventDefault();
  });
  delegate(root, 'click', MATRIX_POSITION, (e, el) => {
    // The column header's basis Select is its own control.
    if (isNestedControl(e.target, el)) return;
    const position = parseMatrixPosition(el.getAttribute('data-matrix-pos'));
    if (position !== null) selectMatrixAt(position, matrixModifiers(e as MouseEvent));
  });
  delegate(root, 'contextmenu', MATRIX_POSITION, (e, el) => {
    if (isNestedControl(e.target, el)) return;
    const position = parseMatrixPosition(el.getAttribute('data-matrix-pos'));
    if (position === null) return;
    selectMatrixTargetForMenu(position);
    if (matrixSelection.value.selected.length > 0) showContextMenu({ kind: 'matrix-cells' }, e);
  });
  delegate(root, 'keydown', MATRIX_POSITION, (e, el) => {
    const event = e as KeyboardEvent;
    if (isNestedControl(event.target, el)) return;
    const grid = currentMatrixGrid();
    const position = parseMatrixPosition(el.getAttribute('data-matrix-pos'));
    if (grid === null || position === null) return;
    const direction = MATRIX_ARROWS[event.key];
    if (direction !== undefined) {
      event.preventDefault();
      const next = moveMatrixFocus(grid, position, direction);
      // Shift+Arrow extends the selection from its anchor to the new position.
      if (event.shiftKey) selectMatrixAt(next, { toggle: false, range: true });
      focusMatrixPosition(next);
      return;
    }
    const key = activationKey(event);
    const onHeader = position.row < 0 || position.col < 0;
    if (key === 'space' || (key === 'enter' && onHeader)) {
      event.preventDefault();
      selectMatrixAt(position, matrixModifiers(event));
    } else if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
      event.preventDefault();
      selectMatrixTargetForMenu(position);
      if (matrixSelection.value.selected.length === 0) return;
      const box = el.getBoundingClientRect();
      showContextMenu({ kind: 'matrix-cells' }, event, { x: box.left + box.width / 2, y: box.bottom });
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      // Delete acts on the selection, or on the focused cell when nothing is selected.
      if (matrixSelection.value.selected.length === 0 && !onHeader)
        selectMatrixAt(position, { toggle: false, range: false });
      unmapMatrixSelection();
    } else if (event.key === 'Escape' && matrixSelection.value.selected.length > 0) {
      event.preventDefault();
      matrixSelection.value = { selected: [], anchor: matrixSelection.value.anchor };
    }
  });
  delegate(root, 'click', '[data-action="matrix-unmap"]', () => {
    unmapMatrixSelection();
  });
  delegate(root, 'click', '[data-quote-addline]', (e) => {
    const id = (e.target as Element).closest('[data-quote-addline]')?.getAttribute('data-quote-addline');
    if (id !== null && id !== undefined) void addQuoteLineAsLine(id);
  });
  delegate(root, 'click', '[data-quote-ignore]', (e) => {
    const id = (e.target as Element).closest('[data-quote-ignore]')?.getAttribute('data-quote-ignore');
    if (id !== null && id !== undefined) void ignoreQuoteLineClient(id);
  });
  delegate(root, 'click', '[data-vendor-merge]', (e) => {
    const spec = (e.target as Element).closest('[data-vendor-merge]')?.getAttribute('data-vendor-merge');
    if (spec !== null && spec !== undefined) void mergeVendorsClient(spec);
  });

  // --- Analysis (Flow F4/F5) ---
  // Hide the "run a pricing check" prompt on this demand for this viewer (HS2-CNXFTE).
  delegate(root, 'click', '[data-action="pricing-prompt-dismiss"]', () => {
    const id = currentDemandId();
    if (id !== undefined) dismissPricingPrompt(id);
  });
  delegate(root, 'click', '[data-action="run-analysis"]', () => {
    void runAnalysisClient();
  });
  delegate(root, 'change', '[name="analysis-version"]', (e) => {
    const id = (e.target as HTMLSelectElement).value;
    assumptionsDialog.value = null;
    selectedAnalysisId.value = id;
    void loadAnalysisReport(id);
  });
  // The single Summary / Outcomes export → OS share sheet or download (HS2-41ZSXD).
  // Overview shares the pricing-check report; the other panes share the
  // business-office decision summary.
  delegate(root, 'click', '[data-action="share-export"]', (e) => {
    const format = (e.target as Element)
      .closest<HTMLElement>('[data-action="share-export"]')
      ?.getAttribute('data-item-id');
    if (format !== 'pdf' && format !== 'xlsx') return;
    const orgId = me.value?.organizationId;
    const demandId = currentDemandId();
    if (orgId === undefined || demandId === undefined) return;
    const shareAnalysis =
      summaryPane.value === 'overview' && analysisReport.value !== null && selectedAnalysisId.value !== '';
    const url = shareAnalysis
      ? `/api/orgs/${orgId}/demands/${demandId}/analyses/${selectedAnalysisId.value}/export?format=${format}`
      : `/api/orgs/${orgId}/demands/${demandId}/decision-summary/export?format=${format}`;
    const filename = `${shareAnalysis ? 'pricing-check' : 'decision-summary'}.${format}`;
    void shareOrDownload(url, filename).then((ok) => {
      if (!ok) notice.value = 'Could not prepare the export.';
    });
  });
  delegate(root, 'click', '[data-action="edit-analysis-assumptions"]', () => {
    openAssumptionsEditor();
  });
  delegate(root, 'click', '[data-action="cancel-analysis-assumptions"]', () => {
    if (!assumptionsSubmitting.value) assumptionsDialog.value = null;
  });
  delegate(root, 'click', '[data-action="save-analysis-assumptions"]', () => {
    void saveAssumptionsEditor(root);
  });
  delegateCapture(root, 'close', 'dialog[data-action="assumptions-dialog"]', () => {
    assumptionsDialog.value = null;
  });
  // --- Decisions (Flow F5) ---
  delegate(root, 'click', '[data-action="decisions-bulk"]', () => {
    void bulkApplyDecisions(selectedLineIds.value);
  });
  // One line: apply the displayed pricing check's recommendation, citing that
  // version like the multi-line bulk apply does (HS2-A0851K).
  delegate(root, 'click', '[data-action="line-accept-recommendation"]', (_e, el) => {
    const lineId = el.getAttribute('data-item-id');
    if (lineId !== null && lineId !== '') void bulkApplyDecisions([lineId]);
  });
  delegate(root, 'click', '[data-record-outcome]', (e) => {
    const id = (e.target as Element).closest('[data-record-outcome]')?.getAttribute('data-record-outcome');
    if (id !== null && id !== undefined) void recordOutcomeClient(root, id);
  });
  delegate(root, 'click', '[data-action="go-savings"]', () => {
    view.value = 'savings';
    orgSavings.value = null;
    void loadOrgSavings();
  });
  delegate(root, 'click', '[data-action="go-vendors"]', () => {
    openSettingsPage('organization', 'vendors');
  });
  delegate(root, 'click', '[data-vendor-open]', (e) => {
    const id = (e.target as Element).closest('[data-vendor-open]')?.getAttribute('data-vendor-open');
    if (id !== null && id !== undefined) void openVendor(id);
  });
  // Vendors: add from the list header, edit from a vendor's header (HS2-BBZXKM).
  delegate(root, 'click', '[data-action="vendor-new"]', () => {
    openVendorDialog(null);
  });
  delegate(root, 'click', '[data-action="supplier-lead-add"]', (_e, el) => {
    const benchmarkId = el.getAttribute('data-benchmark-id');
    if (benchmarkId !== null) void addSupplierLeadAsVendor(benchmarkId);
  });
  delegate(root, 'click', '[data-action="vendor-edit"]', (_e, el) => {
    const id = el.getAttribute('data-vendor-id');
    if (id !== null) openVendorDialog(id);
  });
  delegate(root, 'click', '[data-action="vendor-cancel"]', () => {
    closeVendorDialog();
  });
  delegate(root, 'click', '[data-action="vendor-save"]', () => {
    void saveVendor(root);
  });
  delegateCapture(root, 'close', 'dialog[data-action="vendor-dialog"]', closeVendorDialog);
  delegate(root, 'click', '[data-action="vendors-back"]', () => {
    vendorDetail.value = null;
    void loadVendors();
  });
  delegate(root, 'click', '[data-action="vendor-detail-section"]', (e) => {
    const section = (e.target as Element).closest<HTMLElement>('[data-action="vendor-detail-section"]')?.dataset
      .segmentValue;
    if (section === 'details' || section === 'quotations' || section === 'purchases')
      vendorDetailSection.value = section;
  });

  // --- Background library (Flow F9) ---
  delegate(root, 'click', '[data-action="go-library"]', () => {
    openSettingsPage('organization', 'knowledge-base');
  });
  // The library header's attachment button opens its hidden file input.
  delegate(root, 'click', '[data-action="lib-upload-open"]', () => {
    root.querySelector<HTMLInputElement>('[data-lib-upload]')?.click();
  });
  delegate(root, 'change', '[data-lib-upload]', (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>('[data-lib-upload]');
    const files = input?.files;
    if (input === null || files === null || files === undefined || files.length === 0) return;
    void (async () => {
      for (const file of Array.from(files)) await uploadBackgroundFile(root, file);
      input.value = '';
    })();
  });
  delegate(root, 'click', '[data-action="lib-new-project"]', () => {
    void (async () => {
      if (await newLibraryProject()) {
        if (settingsTab.value === 'project') await openProjectPage(settingsPage.value);
      }
    })();
  });
  delegate(root, 'click', '[data-action="lib-new-fact"]', () => {
    libraryFactDialogId.value = null;
  });
  delegate(root, 'click', '[data-action="lib-edit-fact"]', (_e, el) => {
    libraryFactDialogId.value = el.getAttribute('data-item-id') ?? el.getAttribute('data-key') ?? undefined;
  });
  delegateCapture(root, 'close', 'dialog[data-action="lib-fact-dialog"]', () => {
    libraryFactDialogId.value = undefined;
  });
  delegate(root, 'click', '[data-action="lib-fact-cancel"]', () => {
    void closeLibraryFactDialog(root);
  });
  delegate(root, 'click', '[data-action="lib-fact-save"]', () => {
    void saveLibraryFact(root);
  });
  delegate(root, 'click', '[data-fact-accept]', (e) => {
    const id = (e.target as Element).closest('[data-fact-accept]')?.getAttribute('data-fact-accept');
    if (id !== null && id !== undefined) void acceptLibraryFact(root, id);
  });
  delegate(root, 'click', '[data-fact-delete]', (e) => {
    const id = (e.target as Element).closest('[data-fact-delete]')?.getAttribute('data-fact-delete');
    if (id !== null && id !== undefined) void deleteLibraryFact(id);
  });
  // Knowledge Base documents select and act like Demand › Documents (HS2-KRH1MW):
  // click selects (Shift/Cmd extend), double-click or Enter opens, Space selects,
  // and right-click or the toolbar "…" opens the document actions.
  const libraryDocIds = (): string[] => libraryDocs.value.map((doc) => doc.id);
  const selectedLibraryIds = (): string[] => selectedLibraryDocIds.value.filter((id) => libraryDocIds().includes(id));
  const applyLibrarySelection = (docId: string, event: MouseEvent | KeyboardEvent): void => {
    const result = selectLine({
      orderedIds: libraryDocIds(),
      selectedIds: selectedLibraryIds(),
      clickedId: docId,
      anchorId: librarySelectionAnchor.value,
      toggle: event.metaKey || event.ctrlKey,
      range: event.shiftKey,
    });
    selectedLibraryDocIds.value = result.selectedIds;
    librarySelectionAnchor.value = result.anchorId;
  };
  delegate(root, 'click', '[data-library-doc-id]', (e, el) => {
    if (!isContentItemActivation(e, el as HTMLElement)) return;
    const docId = el.getAttribute('data-library-doc-id');
    if (docId !== null) applyLibrarySelection(docId, e as MouseEvent);
  });
  delegate(root, 'dblclick', '[data-library-doc-id]', (e, el) => {
    const docId = el.getAttribute('data-library-doc-id');
    if (docId === null) return;
    e.preventDefault();
    void openLibraryDocumentClient(docId);
  });
  // Capture phase, before wireContentItems, so Enter opens and Space selects.
  delegateCapture(root, 'keydown', '[data-library-doc-id]', (e, el) => {
    const key = activationKey(e as KeyboardEvent);
    const docId = el.getAttribute('data-library-doc-id');
    if (docId === null || key === null) return;
    e.preventDefault();
    if (key === 'enter') void openLibraryDocumentClient(docId);
    else applyLibrarySelection(docId, e as KeyboardEvent);
  });
  delegate(root, 'contextmenu', '[data-library-doc-id]', (e, el) => {
    const docId = el.getAttribute('data-library-doc-id');
    if (docId === null) return;
    if (!selectedLibraryIds().includes(docId)) {
      selectedLibraryDocIds.value = [docId];
      librarySelectionAnchor.value = docId;
    }
    showContextMenu({ kind: 'library-documents' }, e);
  });
  const oneSelectedLibraryDoc = (): string | undefined => {
    const ids = selectedLibraryIds();
    return ids.length === 1 ? ids[0] : undefined;
  };
  delegate(root, 'click', '[data-action="library-doc-open-selected"]', () => {
    const docId = oneSelectedLibraryDoc();
    if (docId !== undefined) void openLibraryDocumentClient(docId);
  });
  delegate(root, 'click', '[data-action="library-docs-download"]', () => {
    // One at a time: each open supersedes an in-flight one, so parallel calls would drop all but the last.
    void (async () => {
      for (const docId of selectedLibraryIds()) await openLibraryDocumentClient(docId, true);
    })();
  });
  delegate(root, 'click', '[data-action="library-doc-history"]', () => {
    const docId = oneSelectedLibraryDoc();
    if (docId !== undefined) void showLibraryRevisions(docId);
  });
  delegate(root, 'click', '[data-action="library-docs-archive"], [data-action="library-docs-unarchive"]', (_e, el) => {
    const archive = el.getAttribute('data-action') === 'library-docs-archive';
    void (async () => {
      for (const docId of selectedLibraryIds()) await archiveLibraryDoc(`${docId}:${archive ? '1' : '0'}`);
    })();
  });
  delegateCapture(root, 'close', 'dialog.library-revisions-dialog', () => {
    libraryRevisions.value = null;
  });
  delegate(root, 'click', '[data-lib-revision-download]', (e) => {
    const id = (e.target as Element)
      .closest('[data-lib-revision-download]')
      ?.getAttribute('data-lib-revision-download');
    if (id !== null && id !== undefined) void downloadLibraryRevision(id);
  });
  delegate(root, 'click', '[data-action="library-doc-replace"]', () => {
    const docId = oneSelectedLibraryDoc();
    const input = root.querySelector<HTMLInputElement>('[data-lib-replace-input]');
    if (docId === undefined || input === null) return;
    input.dataset.libraryDocId = docId;
    input.click();
  });
  delegate(root, 'change', '[data-lib-replace-input]', (_e, el) => {
    const input = el as HTMLInputElement;
    const docId = input.dataset.libraryDocId;
    const file = input.files?.[0];
    input.value = '';
    if (docId !== undefined && file !== undefined) void replaceLibraryRevision(docId, file);
  });
  delegate(root, 'click', '[data-source-match]', (_e, el) => {
    const [kind, recordId, lineItemId, status] = (el.getAttribute('data-source-match') ?? '').split(':');
    const orgId = me.value?.organizationId;
    if (
      orgId === undefined ||
      orgId === null ||
      recordId === undefined ||
      recordId === '' ||
      lineItemId === undefined ||
      lineItemId === '' ||
      (kind !== 'inventory' && kind !== 'purchase') ||
      (status !== 'confirmed' && status !== 'rejected')
    )
      return;
    void correctSourceMatchAndReload(orgId, kind, recordId, lineItemId, status);
  });
  // Link any citable row to a line, beyond the suggested candidates (HS2-4TER5Q).
  delegate(root, 'click', '[data-action="source-row-find"]', (_e, el) => {
    const lineItemId = el.getAttribute('data-line-item-id');
    if (lineItemId !== null && lineItemId !== '') void searchSourceRowsForPicker(lineItemId, '');
  });
  let sourceRowSearchTimer: ReturnType<typeof setTimeout> | undefined;
  delegate(root, 'input', '[name="source-row-query"]', (_e, el) => {
    const picker = sourceRowPicker.value;
    if (picker === null) return;
    const query = (el as HTMLElement & { value: string }).value;
    sourceRowPicker.value = { ...picker, query };
    clearTimeout(sourceRowSearchTimer);
    sourceRowSearchTimer = setTimeout(() => {
      void searchSourceRowsForPicker(picker.lineItemId, query);
    }, 250);
  });
  delegate(root, 'click', '[data-action="source-row-pick"]', (_e, el) => {
    const picker = sourceRowPicker.value;
    const orgId = me.value?.organizationId;
    const [kind, recordId] = (el.getAttribute('data-item-id') ?? '').split(':');
    if (picker === null || orgId === undefined || orgId === null || recordId === undefined || recordId === '') return;
    if (kind !== 'inventory' && kind !== 'purchase') return;
    void correctSourceMatchAndReload(orgId, kind, recordId, picker.lineItemId, 'confirmed').then((ok) => {
      if (ok) {
        clearTimeout(sourceRowSearchTimer);
        sourceRowPicker.value = null;
        showToast('Source row matched.');
      }
    });
  });
  delegateCapture(root, 'close', 'dialog.source-row-picker-dialog', () => {
    clearTimeout(sourceRowSearchTimer);
    sourceRowPicker.value = null;
  });

  delegate(root, 'click', '[data-action="cancel-demand"]', () => {
    void (async () => {
      const orgId = me.value?.organizationId;
      const ws = workspace.value?.demand as { id: string } | undefined;
      if (orgId === undefined || orgId === null || ws === undefined) return;
      if (!window.confirm('Cancel this demand?')) return;
      await fetch(`/api/orgs/${orgId}/demands/${ws.id}/cancel`, { method: 'POST', credentials: 'same-origin' });
      await Promise.all([reopenDemandIfShown(ws.id), loadDemands()]);
    })();
  });

  delegate(
    root,
    'click',
    '[data-action="demand-reopen-for-changes"], [data-action="demand-lock-changes"]',
    (_e, el) => {
      const ws = workspace.value?.demand as { id: string } | undefined;
      if (ws === undefined) return;
      void setDemandReopenedForChangesClient(ws.id, el.getAttribute('data-action') === 'demand-reopen-for-changes');
    },
  );
  // The sidebar's Return to Requester… opens the demand, then its return dialog (HS2-T90PS1).
  delegate(root, 'click', '[data-action="demand-return-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id === null || id === '') return;
    void openDemand(id).then(() => {
      if (currentDemandId() === id) returnDialogOpen.value = true;
    });
  });
  delegate(root, 'click', '[data-action="demand-duplicate"]', () => {
    const ws = workspace.value?.demand as { id: string } | undefined;
    if (ws !== undefined) void duplicateDemandClient(ws.id);
  });
  delegate(root, 'click', '[data-action="demand-duplicate-nav"]', (_e, el) => {
    const id = el.getAttribute('data-demand-id');
    if (id !== null && id !== '') void duplicateDemandClient(id);
  });
  delegate(root, 'click', '[data-action="demand-withdraw"]', () => {
    const ws = workspace.value?.demand as { id: string } | undefined;
    if (ws === undefined) return;
    if (!window.confirm('Withdraw this demand to a draft? You can edit it and submit it again.')) return;
    void withdrawDemandClient(ws.id);
  });
  delegate(root, 'click', '[data-action="reopen-demand"]', () => {
    void (async () => {
      const orgId = me.value?.organizationId;
      const ws = workspace.value?.demand as { id: string } | undefined;
      if (orgId === undefined || orgId === null || ws === undefined) return;
      await fetch(`/api/orgs/${orgId}/demands/${ws.id}/reopen`, { method: 'POST', credentials: 'same-origin' });
      await Promise.all([reopenDemandIfShown(ws.id), loadDemands()]);
    })();
  });

  delegate(root, 'click', '[data-form-answer]', (e) => {
    const id = (e.target as Element).closest('[data-form-answer]')?.getAttribute('data-form-answer');
    if (id !== null && id !== undefined) void answerFormClient(root, id);
  });
  delegate(root, 'click', '[data-form-skip]', (e) => {
    const id = (e.target as Element).closest('[data-form-skip]')?.getAttribute('data-form-skip');
    if (id !== null && id !== undefined) void skipFormClient(id);
  });

  delegate(root, 'click', '[data-action="save-profile"]', () => {
    void saveProfile(root);
  });
  // Settings › Members: "New Member…" opens the invitation dialog (HS2-FJ9J2K).
  delegate(root, 'click', '[data-action="member-new"]', () => {
    openInviteDialog();
  });
  delegate(root, 'click', '[data-action="member-open"]', (_e, el) => {
    const memberId = el.getAttribute('data-item-id');
    if (memberId !== null) openMemberDialog(memberId);
  });
  delegateCapture(root, 'close', 'dialog[data-action="member-dialog"]', closeMemberDialog);
  delegate(root, 'click', '[data-action="member-dialog-cancel"]', () => {
    closeMemberDialog();
  });
  delegateCapture(root, 'close', 'dialog[data-action="member-invite-dialog"]', closeMemberDialog);
  delegate(root, 'click', '[data-action="member-invite"]', () => {
    void inviteMember(root);
  });
  delegate(root, 'click', '[data-action="member-invite-resend"]', (_e, el) => {
    const memberId = el.getAttribute('data-member-id');
    if (memberId !== null) void resendInvitation(memberId);
  });
  delegate(root, 'click', '[data-action="member-roles-save"]', (_e, el) => {
    const memberId = el.getAttribute('data-member-id');
    if (memberId !== null) void saveMemberRoles(root, memberId);
  });
  delegate(root, 'click', '[data-action="member-remove"]', (_e, el) => {
    const memberId = el.getAttribute('data-member-id');
    if (memberId !== null) void removeMember(memberId);
  });
  // Personal preference: number grouping (HS2-ZHZ39R). Saved immediately on change
  // and re-loaded into `me` so currency amounts reformat app-wide.
  delegate(root, 'change', '[name="pref-grouping"]', (e) => {
    const v = (e.target as HTMLSelectElement).value;
    void (async () => {
      const res = await api.updatePreferences({
        numberGrouping: v === 'international' || v === 'pakistani' ? v : null,
      });
      // Merge the saved preferences into `me` locally so currency amounts reformat without
      // a full loadMe() (which would re-derive the view and leave Settings).
      if (res.ok && res.data !== null && me.value !== null) {
        me.value = { ...me.value, preferences: res.data.preferences };
        notice.value = '';
        showToast('Preference saved.');
      } else if (!res.ok) notice.value = res.error ?? 'Could not save the preference.';
    })();
  });
  delegate(root, 'click', '[data-action="save-thresholds"]', () => {
    void saveThresholds(root);
  });
  delegate(root, 'click', '[data-action="currency-picker-open"]', () => {
    currencyPickerFilter.value = '';
    currencyPickerOpen.value = true;
  });
  delegate(root, 'click', '[data-action="currency-filter-clear"]', () => {
    const editor = root.querySelector<HTMLElement>('[data-search-field-editor="currency-filter"]');
    if (editor !== null) editor.textContent = '';
    currencyPickerFilter.value = '';
  });
  delegate(root, 'click', '[data-action="currency-select"]', (_e, el) => {
    if (el.hasAttribute('disabled')) return; // the base currency or one already in use (HS2-5GWEXJ)
    const code = el.getAttribute('data-currency');
    if (code !== null) selectFxCurrency(code);
  });
  delegateCapture(root, 'close', 'dialog[data-action="currency-picker-dialog"]', () => {
    currencyPickerOpen.value = false;
  });
  delegate(root, 'click', '[data-action="currency-edit-open"]', (_e, el) => {
    currencyEditCode.value = el.getAttribute('data-currency');
  });
  delegateCapture(root, 'close', 'dialog[data-action="currency-edit-dialog"]', () => {
    currencyEditCode.value = null;
  });
  delegate(root, 'click', '[data-action="currency-save"]', () => {
    void saveFxCurrency(root);
  });
  delegate(root, 'click', '[data-action="currency-remove"]', () => {
    void saveFxCurrency(root, true);
  });
  delegate(root, 'click', '[data-action="save-assumptions"]', () => {
    void saveAssumptions(root);
  });
  delegate(root, 'click', '[data-action="edit-ai-budget"]', () => {
    aiBudgetDialogOpen.value = true;
  });
  delegateCapture(root, 'close', 'dialog[data-action="ai-budget-dialog"]', () => {
    aiBudgetDialogOpen.value = false;
  });
  delegate(root, 'click', '[data-action="ai-spend-view"]', (e) => {
    const view = (e.target as Element).closest<HTMLElement>('[data-action="ai-spend-view"]')?.dataset.segmentValue;
    if (view === 'day' || view === 'analysis') aiSpendView.value = view;
  });
  delegate(root, 'click', '[data-action="savings-breakdown-view"]', (e) => {
    const selected = (e.target as Element).closest<HTMLElement>('[data-action="savings-breakdown-view"]')?.dataset
      .segmentValue;
    if (selected === 'demand' || selected === 'vendor' || selected === 'month') savingsBreakdownView.value = selected;
  });
  delegate(root, 'click', '[data-action="save-ai-budget"]', () => {
    void saveAiBudget(root);
  });
  delegate(root, 'click', '[data-action="export-org"]', () => {
    void exportOrgClient();
  });
  delegate(root, 'click', '[data-action="delete-org-open"]', () => {
    deleteConfirmOpen.value = true;
  });
  delegateCapture(root, 'close', 'dialog[data-action="delete-org-dialog"]', () => {
    deleteConfirmOpen.value = false;
  });
  delegate(root, 'click', '[data-action="delete-org-confirm"]', () => {
    void deleteOrgClient(root);
  });
  // Settings › Sites: add from the header, edit from a row, in one dialog (HS2-DE4RP1).
  delegate(root, 'click', '[data-action="site-new"]', () => {
    openSiteDialog(null);
  });
  delegate(root, 'click', '[data-action="site-edit"]', (_e, el) => {
    const id = el.getAttribute('data-item-id');
    if (id !== null) openSiteDialog(id);
  });
  delegate(root, 'click', '[data-action="site-save"]', () => {
    void saveSite(root);
  });
  delegateCapture(root, 'close', 'dialog[data-action="site-dialog"]', closeSiteDialog);

  wireAppHistory({ hold: (navigation) => holdNavigation(root, navigation) });
  void loadMe();
}

/** Confirm or reject a source row for a line, then refresh the demand's evidence. */
async function correctSourceMatchAndReload(
  orgId: string,
  kind: 'inventory' | 'purchase',
  recordId: string,
  lineItemId: string,
  status: 'confirmed' | 'rejected',
): Promise<boolean> {
  const result = await api.correctSourceMatch(orgId, kind, recordId, lineItemId, status);
  if (!result.ok) {
    notice.value = result.error ?? 'Could not update source match.';
    return false;
  }
  const demandId = (workspace.value?.demand as { id?: string } | undefined)?.id;
  if (demandId !== undefined) await reloadSourceEvidence(demandId);
  return true;
}
